import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Button,
  Modal,
  Toast,
  Frame,
  TextField,
  Select,
  Spinner,
  Text,
  Page,
  Layout,
  Tabs,
  Badge,
  DataTable,
  Icon,
  Card,
  Autocomplete,
  Collapsible,
  ButtonGroup,
  BlockStack,
  Box,
  Tooltip,
  FormLayout, DatePicker, Pagination, Popover
} from "@shopify/polaris";

import {
  MoneyNoneIcon,
  DiscountIcon,
  CheckSmallIcon,
  AlertBubbleIcon,
  PersonalizedTextIcon,
  MinusIcon,
  WalletIcon,
  TransactionIcon,
  LocationIcon,
  GlobeIcon,
  ClockIcon,
  EditIcon,
  DeleteIcon,
  ViewIcon,
  PaymentIcon,
  BankIcon,
  CashDollarIcon, PlusIcon, ChevronDownIcon,
  CalendarIcon, OrderIcon, XIcon, RewardIcon, SettingsIcon, ExportIcon, ChartVerticalIcon, ReceiptIcon, StatusActiveIcon
} from "@shopify/polaris-icons";

import { useNavigate, useParams } from "react-router-dom";
import useAxiosClient from '../axiosClient';
import { useTranslation } from 'react-i18next';
import PhotoAvatar from './PhotoAvatar';
import SegmentedTabs from '../SegmentedTabs';
import { ColumnHeading } from '../ui/CardSurface';

// Complex Tooltip Component
const ComplexTooltip = ({ content, children }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const contentRef = useRef(null);

  useEffect(() => {
    if (contentRef.current) {
      const isOverflowing = contentRef.current.scrollWidth > contentRef.current.clientWidth;
      setShowTooltip(isOverflowing);
    }
  }, [content]);

  return showTooltip ? (
    <Tooltip active content={content}>
      <span ref={contentRef} className="block truncate">
        {children}
      </span>
    </Tooltip>
  ) : (
    <span ref={contentRef} className="block truncate">
      {children}
    </span>
  );
};
const DateRangePicker = ({ dateRange, setDateRange, addSalaryEnabled, firstClockInDate }) => {
  const { t } = useTranslation();
  const [{ month, year }, setDate] = useState({
    month: dateRange.start.getMonth(),
    year: dateRange.start.getFullYear(),
  });
  const [open, setOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState('last30');
  const containerRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const noteText = addSalaryEnabled && firstClockInDate
    ? `${t('note')}: ${t('when_salary_is_enabled_data_starts_from_first_clock_in')} (${new Date(firstClockInDate).toLocaleDateString()})`
    : "";

  const applyFilter = (filterKey) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let start, end;

    switch (filterKey) {
      case 'today':
        start = new Date(today);
        end = new Date(today);
        break;
      case 'yesterday': {
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        start = yesterday;
        end = yesterday;
        break;
      }
      case 'last7':
        start = new Date(today);
        start.setDate(start.getDate() - 6);
        end = new Date(today);
        break;
      case 'last30':
        start = new Date(today);
        start.setDate(start.getDate() - 29);
        end = new Date(today);
        break;
      case 'thisWeek': {
        const dayOfWeek = today.getDay();
        start = new Date(today);
        start.setDate(today.getDate() - dayOfWeek);
        end = new Date(today);
        break;
      }
      case 'lastWeek': {
        const dayOfWeek = today.getDay();
        start = new Date(today);
        start.setDate(today.getDate() - dayOfWeek - 7);
        end = new Date(start);
        end.setDate(start.getDate() + 6);
        break;
      }
      case 'thisMonth':
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        end = new Date(today);
        break;
      case 'lastMonth':
        start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        end = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      case 'thisYear':
        start = new Date(today.getFullYear(), 0, 1);
        end = new Date(today);
        break;
      default:
        return;
    }

    setActiveFilter(filterKey);
    setDateRange({ start, end });
    setDate({ month: start.getMonth(), year: start.getFullYear() });
  };

  const filters = [
    { key: 'today', label: t('today') || 'Today' },
    { key: 'yesterday', label: t('yesterday') || 'Yesterday' },
    { key: 'last7', label: t('last_7_days') || 'Last 7 Days' },
    { key: 'last30', label: t('last_30_days') || 'Last 30 Days' },
    { key: 'thisWeek', label: t('this_week') || 'This Week' },
    { key: 'lastWeek', label: t('last_week') || 'Last Week' },
    { key: 'thisMonth', label: t('this_month') || 'This Month' },
    { key: 'lastMonth', label: t('last_month') || 'Last Month' },
    { key: 'thisYear', label: t('this_year') || 'This Year' },
  ];

  return (
    <div ref={containerRef} style={{ position: 'relative', display: 'inline-block' }}>
      {/* Trigger button */}
      <button
        onClick={() => setOpen(!open)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 14px',
          fontSize: '14px',
          fontWeight: 500,
          color: '#202223',
          background: '#ffffff',
          border: '1px solid #c9cccf',
          borderRadius: '8px',
          cursor: 'pointer',
          transition: 'all 0.15s ease',
          boxShadow: open ? '0 0 0 2px rgba(16, 185, 129, 0.2)' : '0 1px 2px rgba(0,0,0,0.05)',
          borderColor: open ? '#10b981' : '#c9cccf',
        }}
      >
        <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M5 3a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2V5a2 2 0 00-2-2H5zm0 1.5h10a.5.5 0 01.5.5v1.5h-11V5a.5.5 0 01.5-.5zm-.5 3.5h11V15a.5.5 0 01-.5.5H5a.5.5 0 01-.5-.5V8z" fill="#6d7175"/>
        </svg>
        <span>{dateRange.start.toLocaleDateString()} - {dateRange.end.toLocaleDateString()}</span>
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ marginLeft: '2px' }}>
          <path d="M3 4.5L6 7.5L9 4.5" stroke="#6d7175" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {/* Dropdown panel */}
      {open && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 6px)',
          right: 0,
          zIndex: 999,
          display: 'flex',
          background: '#ffffff',
          borderRadius: '12px',
          border: '1px solid #e1e3e5',
          boxShadow: '0 8px 28px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)',
          overflow: 'hidden',
          animation: 'datePickerFadeIn 0.15s ease-out',
          // 170px of presets + 360px of calendar. With no width the panel inherits a
          // container narrower than its two columns and the calendar wraps underneath,
          // leaving dead space; nowrap keeps them on one row.
          width: '530px',
          maxWidth: 'calc(100vw - 24px)',
          flexWrap: 'nowrap',
          alignItems: 'stretch',
          boxSizing: 'border-box',
        }}>
          {/* Left: Filter presets */}
          <div style={{
            width: '170px',
            borderRight: '1px solid #e8eaeb',
            padding: '8px 0',
            background: '#fafbfc',
            flexShrink: 0,
          }}>
            <div style={{
              padding: '8px 16px 12px',
              fontSize: '11px',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: '#6d7175',
            }}>
              {t('date_range') || 'Date Range'}
            </div>
            {filters.map((filter) => (
              <button
                key={filter.key}
                onClick={() => applyFilter(filter.key)}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '9px 16px',
                  border: 'none',
                  background: activeFilter === filter.key ? '#e6f7f0' : 'transparent',
                  color: activeFilter === filter.key ? '#047857' : '#202223',
                  fontWeight: activeFilter === filter.key ? 600 : 400,
                  fontSize: '13.5px',
                  textAlign: 'left',
                  cursor: 'pointer',
                  borderLeft: activeFilter === filter.key ? '3px solid #10b981' : '3px solid transparent',
                  transition: 'all 0.12s ease',
                }}
                onMouseEnter={(e) => {
                  if (activeFilter !== filter.key) {
                    e.target.style.background = '#f1f2f3';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeFilter !== filter.key) {
                    e.target.style.background = 'transparent';
                  }
                }}
              >
                {filter.label}
              </button>
            ))}
          </div>

          {/* Right: Calendar */}
          <div style={{ width: '360px', flex: '0 0 360px', boxSizing: 'border-box', padding: '16px 20px', minWidth: 0 }}>
            <DatePicker
              month={month}
              year={year}
              onChange={({ start, end }) => {
                setDateRange({ start, end });
                setActiveFilter('custom');
              }}
              onMonthChange={(m, y) => setDate({ month: m, year: y })}
              selected={dateRange}
              allowRange
              multiMonth={false}
            />
            {noteText && (
              <div style={{ padding: '8px 0', fontSize: '12px', color: '#6d7175', fontStyle: 'italic' }}>
                {noteText}
              </div>
            )}
            {/* Footer with selected range and apply */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '14px',
              paddingTop: '14px',
              borderTop: '1px solid #e8eaeb',
            }}>
              <span style={{ fontSize: '13px', color: '#374151', fontWeight: 500 }}>
                {dateRange.start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                {' — '}
                {dateRange.end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </span>
              <button
                onClick={() => setOpen(false)}
                style={{
                  padding: '7px 18px',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#ffffff',
                  background: '#10b981',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  transition: 'background 0.15s ease',
                }}
                onMouseEnter={(e) => e.target.style.background = '#059669'}
                onMouseLeave={(e) => e.target.style.background = '#10b981'}
              >
                {t('apply') || 'Apply'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const GpsCoordinateLink = ({ latitude, longitude, label }) => {
  if (latitude == null || longitude == null || latitude === '' || longitude === '') {
    return null;
  }

  const lat = Number(latitude);
  const lng = Number(longitude);
  if (Number.isNaN(lat) || Number.isNaN(lng)) {
    return null;
  }

  const mapsUrl = `https://www.google.com/maps?q=${lat},${lng}`;

  return (
    <div className="text-xs text-[#6d7175] mt-0.5">
      <span className="font-medium">{label}: </span>
      <a
        href={mapsUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="text-[#2c6ecb] hover:underline"
      >
        {lat.toFixed(6)}, {lng.toFixed(6)}
      </a>
    </div>
  );
};

const StaffAttendanceGps = ({ record, showGps, t }) => {
  if (!showGps || record.status !== 'Work') {
    return null;
  }

  const hasClockInGps = record.clock_in_latitude != null && record.clock_in_longitude != null;
  const hasClockOutGps = record.clock_out_latitude != null && record.clock_out_longitude != null;

  if (!hasClockInGps && !hasClockOutGps) {
    return null;
  }

  return (
    <div className="mt-1 space-y-0.5">
      {hasClockInGps && (
        <GpsCoordinateLink
          latitude={record.clock_in_latitude}
          longitude={record.clock_in_longitude}
          label={t('clock_in_gps')}
        />
      )}
      {hasClockOutGps && (
        <GpsCoordinateLink
          latitude={record.clock_out_latitude}
          longitude={record.clock_out_longitude}
          label={t('clock_out_gps')}
        />
      )}
    </div>
  );
};

const StaffDetails = () => {
  const { t } = useTranslation();
  const axiosClient = useAxiosClient();
  const navigate = useNavigate();
  const { id } = useParams();
  const [selectedTab, setSelectedTab] = useState(0);
  const [toastActive, setToastActive] = useState(false);
  const toggleToastActive = useCallback(() => setToastActive((toastActive) => !toastActive), []);
  const [staffData, setStaffData] = useState('');
  const [clockData, setClockData] = useState('');
  const [hoursFormat, setHoursFormat] = useState('standard');
  const [gpsLocationEnabled, setGpsLocationEnabled] = useState(false);
  const [filteredClockData, setFilteredClockData] = useState(''); // New state for filtered data
  const { currencyData } = window.appGlobals || { currencyData: {} };
  const [isLoading, setIsLoading] = useState(true);
  const [toastProps, setToastProps] = useState({ active: false, content: '', error: false });
  const [selectedProgramId, setSelectedProgramId] = useState('all');
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  // State for editing clock times
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isViewEntriesModalOpen, setIsViewEntriesModalOpen] = useState(false);
  const [selectedClock, setSelectedClock] = useState(null);
  const [dateEntries, setDateEntries] = useState([]);
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [editedStartTime, setEditedStartTime] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [editedEndTime, setEditedEndTime] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [commissionData, setCommissionData] = useState({
    commission_orders: [],
    active_programs: [],
    stats: {
      total_commission: 0,
      total_paid_commission: 0,
      total_unpaid_commission: 0,
      total_orders: 0
    },
    loading: true
  });
  const [dateRange, setDateRange] = useState(() => {
    const endDate = new Date(); // Current date
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 30); // Set to 30 days ago

    return {
      start: startDate,
      end: endDate
    };
  });

  // Add state for add_salary_enabled and firstClockInDate
  const [addSalaryEnabled, setAddSalaryEnabled] = useState(false);
  const [firstClockInDate, setFirstClockInDate] = useState(null);

  const [selectedOrderDetails, setSelectedOrderDetails] = useState(null);
  const handleTabChange = useCallback(
    (selectedTabIndex) => setSelectedTab(selectedTabIndex),
    []
  );
  // SegmentedTabs reads `label` and `icon`; Polaris <Tabs> read `content`. The
  // component was swapped without the data, so every tab rendered as an empty
  // button. `content` is kept so nothing else that still reads it breaks.
  const tabs = [
    {
      id: "overview-1",
      panelID: "overview-content-1",
      label: t('overview'),
      content: t('overview'),
      icon: ChartVerticalIcon,
    },
    {
      id: "payroll-1",
      panelID: "payroll-content-1",
      label: t('payroll'),
      content: t('payroll'),
      icon: ReceiptIcon,
    },
  ];
  const formatCurrency = (value) => {
    return (currencyData[staffData?.currency]?.symbol || '') +
      Number(value).toFixed(2);
  };
  // Shows a toast message
  const showToast = (content, isError = false) => {
    setToastProps({ active: true, content, error: isError });
    setTimeout(() => setToastProps({ active: false, content: '', error: false }), 3000);
  };

  const calculateDayTotal = (start, end) => {
    if (!start || !end) return "N/A";

    // Split the time strings and convert each part to a number.
    const startParts = start.split(':').map(Number);
    const endParts = end.split(':').map(Number);

    // If seconds are not provided, default to 0.
    const startHours = startParts[0] || 0;
    const startMinutes = startParts[1] || 0;
    const startSeconds = startParts[2] || 0;

    const endHours = endParts[0] || 0;
    const endMinutes = endParts[1] || 0;
    const endSeconds = endParts[2] || 0;

    // Convert start and end times to total seconds from midnight.
    const startTotalSeconds = startHours * 3600 + startMinutes * 60 + startSeconds;
    const endTotalSeconds = endHours * 3600 + endMinutes * 60 + endSeconds;

    // Calculate the difference in seconds. Adjust if the shift crosses midnight.
    let totalSeconds = endTotalSeconds - startTotalSeconds;
    if (totalSeconds < 0) totalSeconds += 24 * 3600; // Handle overnight shifts

    // Convert total seconds back to hours, minutes, and seconds.
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return `${hours}h ${minutes}m ${seconds}s`;
  };

  const formatSecondsToHoursString = (timeStr) => {
    if (!timeStr || timeStr === '00:00:00') return hoursFormat === 'decimal' ? '0.00 hours' : '00:00:00';
    if (hoursFormat === 'decimal') {
      const parts = timeStr.split(':').map(Number);
      if (parts.length === 3) {
        const decimalHours = parts[0] + (parts[1] / 60) + (parts[2] / 3600);
        return `${decimalHours.toFixed(2)} hours`;
      }
    }
    return timeStr;
  };

  // Transform clock data for DataTable
  const getClockRows = useCallback(() => {
    if (!Array.isArray(filteredClockData)) return [];

    // Get the current page data
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedData = filteredClockData.slice(startIndex, endIndex);

    return paginatedData.map(record => [
      // Date Column - Hide on small screens
      <div className="hidden md:block">
        {record.formatted_date || formatDate(record.current_date)}
      </div>,

      // Status Column - NEW
      <div className="text-center">
        {record.status === 'Work' ? (
          <Badge tone="success">{t('work')}</Badge>
        ) : record.status === 'Leave' ? (
          <Badge tone="info">{t('leave')}</Badge>
        ) : record.status === 'Holiday' ? (
          <Badge tone="info">{t('holiday')}</Badge>
        ) : (
          <Badge tone="critical">{t('absent')}</Badge>
        )}
      </div>,

      // Location Column - Full width on small screens
      <div className="w-full rounded-lg hover:border-[#2c6ecb] transition-all duration-200 flex justify-left">
        <div className="flex items-center gap-2 ">
          <div className="p-1 rounded-full bg-[#f1f8ff] hidden sm:block">
            <Icon
              source={LocationIcon}
              color="primary"
              tone="strong"
            />
          </div>
          <div className="min-w-0 flex-1">
            {record.status === 'Work' ? (
              <>
                <div className="font-medium text-[#202223] truncate">
                  {record.location_name || 'N/A'}
                </div>
                <div className="text-xs text-[#6d7175] mt-0.5 truncate hidden sm:block">
                  {record.location_address || 'N/A'}
                </div>
                {record.timezone && record.timezone_out && record.timezone_out !== record.timezone ? (
                  <Tooltip
                    content={t('timezone_mismatch_hint', {
                      defaultValue:
                        'Clocked in and out in different timezones. Total hours reflect the actual time elapsed, not the gap between the displayed in/out times.',
                    })}
                  >
                    <div className="text-xs text-[#b98900] mt-0.5 flex items-center gap-1 cursor-help">
                      <span className="inline-flex w-3.5 h-3.5 shrink-0">
                        <Icon source={GlobeIcon} color="warning" />
                      </span>
                      <span className="truncate">{record.timezone} → {record.timezone_out}</span>
                    </div>
                  </Tooltip>
                ) : record.timezone ? (
                  <div className="text-xs text-[#6d7175] mt-0.5 truncate flex items-center gap-1" title={record.timezone}>
                    <span className="inline-flex w-3.5 h-3.5 shrink-0">
                      <Icon source={GlobeIcon} color="subdued" />
                    </span>
                    <span className="truncate">{t('timezone', { defaultValue: 'Timezone' })}: {record.timezone}</span>
                  </div>
                ) : null}
                <StaffAttendanceGps record={record} showGps={gpsLocationEnabled} t={t} />
              </>
            ) : record.status === 'Leave' ? (
              <>
                <div className="font-medium text-[#202223] truncate">
                  {record.leave_details?.policy_name || 'Leave'}
                </div>
                <div className="text-xs text-[#6d7175] mt-0.5 truncate hidden sm:block">
                  {record.leave_details?.paid_status || 'N/A'} - {record.leave_details?.reason || 'N/A'}
                </div>
              </>
            ) : (
              <div className="font-medium text-[#202223] truncate">
                {t('not_available')}
              </div>
            )}
          </div>
        </div>
      </div>,

      // Break Time Column
      <div className="text-center flex items-center gap-1 justify-left whitespace-nowrap">
        {record.status === 'Work' ? (
          <>
            <div className="m-0 flex">
              <Icon
                source={ClockIcon}
                color="critical"
                className="w-4 h-4 sm:w-5 sm:h-5"
              />
            </div>
            <span className="font-medium text-[#202223] truncate">
              {formatSecondsToHoursString(record.total_break_time)}
            </span>
          </>
        ) : (
          <span className="font-medium text-[#202223] truncate">
            N/A
          </span>
        )}
      </div>,

      // Clock In Column
      <div className="text-center flex items-center gap-2 justify-left whitespace-nowrap">
        {record.status === 'Work' ? (
          <>
            {record.first_in_photo && (
              <PhotoAvatar
                photo={record.first_in_photo}
                label={`Clock In - ${formatTime(record.start_at)}`}
                size="small"
              />
            )}
            <div className="flex items-center gap-1">
              <div className="m-0 flex">
                <Icon
                  source={ClockIcon}
                  color="success"
                  className="w-4 h-4 sm:w-5 sm:h-5"
                />
              </div>
              <span className="font-medium text-[#202223] truncate">
                {formatTime(record.start_at)}
              </span>
            </div>
          </>
        ) : (
          <span className="font-medium text-[#202223] truncate">
            N/A
          </span>
        )}
      </div>,

      // Clock Out Column
      <div className="text-center flex items-center gap-2 justify-left">
        {record.status === 'Work' ? (
          record.end_at ? (
            <>
              {record.last_out_photo && (
                <PhotoAvatar
                  photo={record.last_out_photo}
                  label={`Clock Out - ${formatTime(record.end_at)}`}
                  size="small"
                />
              )}
              <div className="min-w-0 flex flex-col items-start">
                <div className="flex items-center gap-1 whitespace-nowrap">
                  <div className="m-0 flex">
                    <Icon
                      source={ClockIcon}
                      color="critical"
                      className="w-4 h-4 sm:w-5 sm:h-5"
                    />
                  </div>
                  <span className="font-medium text-[#202223] truncate">
                    {formatTime(record.end_at)}
                  </span>
                </div>
                {record.clock_out_note && (
                  <div className="text-xs text-[#6d7175] mt-0.5 max-w-[220px] truncate" title={record.clock_out_note}>
                    {t('clock_out_note')}: {record.clock_out_note}
                  </div>
                )}
              </div>
            </>
          ) : (
            <Button
              size="slim"
              onClick={() => handleEditClick(record)}
              icon={ClockIcon}
            >
              {t('add_clock_out')}
            </Button>
          )
        ) : (
          <span className="font-medium text-[#202223] truncate">
            N/A
          </span>
        )}
      </div>,

      // Total Hours Column
      <div className="text-center flex items-center gap-1 justify-left whitespace-nowrap">
        {record.status === 'Work' ? (
          record.end_at ? (
            <div className="font-medium bg-[#f1f8ff] text-[#2c6ecb] px-2 py-1 sm:px-3 sm:py-1 rounded-full inline-block text-sm sm:text-sm">
              {formatSecondsToHoursString(record.total_time_worked)}
            </div>
          ) : (
            <Badge tone="attention">{t('pending')}</Badge>
          )
        ) : record.status === 'Leave' ? (
          record.leave_details?.paid_status === 'Paid' ? (
            <div className="font-medium bg-[#ebf9eb] text-[#108043] px-2 py-1 sm:px-3 sm:py-1 rounded-full inline-block text-sm sm:text-sm">
              {t('paid_leave')}
            </div>
          ) : (
            <div className="font-medium bg-[#fff8e6] text-[#b98900] px-2 py-1 sm:px-3 sm:py-1 rounded-full inline-block text-sm sm:text-sm">
              {t('unpaid_leave')}
            </div>
          )
        ) : record.status === 'Holiday' ? (
          <div className="font-medium bg-[#f1f8ff] text-[#2c6ecb] px-2 py-1 sm:px-3 sm:py-1 rounded-full inline-block text-sm sm:text-sm">
            {t('holiday')}
          </div>
        ) : (
          <div className="font-medium bg-[#fbe9e9] text-[#d72c0d] px-2 py-1 sm:px-3 sm:py-1 rounded-full inline-block text-sm sm:text-sm">
            {t('absent')}
          </div>
        )}
      </div>,

      // Show view, edit and delete buttons for Work entries
      record.status === 'Work' ? (
        <ButtonGroup>
          <Button
            size="slim"
            onClick={() => handleViewEntriesClick(record)}
            icon={ViewIcon}
          />
          {(record.start_at || record.end_at) && (
            <>

              <Button
                size="slim"
                tone="critical"
                onClick={() => handleDeleteClick(record)}
                icon={DeleteIcon}
              />
            </>
          )}
        </ButtonGroup>
      ) : null
    ]);
  }, [filteredClockData, currentPage, itemsPerPage, hoursFormat, gpsLocationEnabled, t]);

  // Handle edit button click
  // Handle edit button click
  const handleEditClick = (record) => {
    setSelectedClock(record);
    // Parse time values from HH:MM:SS to HH:MM format for the inputs
    setEditedStartTime(record.start_at || '00:00:00');
    // If end_at is missing, set current time as default for clock out
    const now = new Date();
    const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
    setEditedEndTime(record.end_at || currentTime);
    setIsEditModalOpen(true);
  };

  // Handle view entries button click
  const handleViewEntriesClick = async (record) => {
    setSelectedClock(record);
    setIsViewEntriesModalOpen(true);
    setLoadingEntries(true);

    try {
      const response = await axiosClient.post('/entries', {
        staffId: id,
        date: record.current_date,
      });

      if (response.data.success) {
        setDateEntries(response.data.history || []);
      } else {
        showToast('Failed to load entries', true);
        setDateEntries([]);
      }
    } catch (error) {
      showToast('Failed to load entries', true);
      setDateEntries([]);
    } finally {
      setLoadingEntries(false);
    }
  };

  // Handle delete button click
  const handleDeleteClick = (record) => {
    setSelectedClock(record);
    setIsDeleteModalOpen(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!selectedClock) return;

    setDeleteLoading(true);
    try {
      const response = await axiosClient.delete('/clock/delete-by-date', {
        data: {
          staffId: id,
          date: selectedClock.current_date,
          shopDomain: window.shopOrigin
        }
      });

      if (response.data.success) {
        // Show success message
        showToast(response.data.message);

        // Close modal and reset state
        setIsDeleteModalOpen(false);
        setSelectedClock(null);

        // Refresh clock data
        await fetchClockDetails();
      } else {
        showToast(response.data.message || 'Failed to delete entries', true);
      }
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to delete clock entries', true);
    } finally {
      setDeleteLoading(false);
    }
  };


  // Format date helper function
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Format time helper function
  const formatTime = (timeString) => {
    if (!timeString) return "N/A";

    const [hours, minutes] = timeString.split(':');
    const date = new Date();
    date.setHours(hours, minutes);

    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  // Parse time string to Time object for TimePicker
  const parseTimeString = (timeString) => {
    if (!timeString) return { hour: 0, minute: 0 };
    const [hours, minutes] = timeString.split(':').map(Number);
    return {
      hour: hours,
      minute: minutes
    };
  };

  // Format Time object back to time string
  const formatTimeObject = (timeObj) => {
    if (!timeObj) return '';
    return `${timeObj.hour.toString().padStart(2, '0')}:${timeObj.minute.toString().padStart(2, '0')}`;
  };

  const fetchStaffDetails = async () => {
    try {
      setIsLoading(true);
      const response = await axiosClient.get(`/staff/${id}`);
      if (response.data.success) {
        setStaffData(response.data.staff);
      } else {
        showToast(t('failed_to_fetch_staff_details'), true);
        shopify.toast.show('Failed to fetch staff details', {
          isError: true
        });
      }
    } catch (error) {
      showToast(t('failed_to_fetch_staff_details'), true);
      shopify.toast.show('Failed to fetch staff details', {
        isError: true
      });
    } finally {
      setIsLoading(false);
    }
  };

  const [statsData, setStatsData] = useState({
    total_hours: "0h 0m 0s",
    working_hours: "0h 0m 0s",
    total_break_time: "0h 0m 0s",
    total_absents: 0,
    total_earnings: 0,
    total_paid: 0,
    total_unpaid: 0,
    total_transactions: 0,
    recent_transactions: [],
    total_leaves: 0,
    paid_leaves: 0,
    unpaid_leaves: 0
  });

  const fetchClockDetails = async () => {
    try {
      setIsLoading(true);
      const response = await axiosClient.get(`/clock/${id}`, {
        params: {
          start_date: dateRange.start.toLocaleDateString("en-US"),
          end_date: dateRange.end.toLocaleDateString("en-US")
        }
      });

      if (response.data.success) {
        if (response.data.hours_format) {
          setHoursFormat(response.data.hours_format);
        }
        if (response.data.gps_location_enabled !== undefined) {
          setGpsLocationEnabled(!!response.data.gps_location_enabled);
        }
        if (Array.isArray(response.data.clock)) {
          setClockData(response.data.clock);
          // Reset to first page when data changes
          setCurrentPage(1);
        }
        if (response.data.stats) {
          setStatsData(response.data.stats);

          // Set add_salary_enabled flag from backend response
          if (response.data.stats.add_salary_enabled !== undefined) {
            setAddSalaryEnabled(response.data.stats.add_salary_enabled);
          }

          // Set first clock-in date if available in date_range or directly from response
          if (response.data.stats.first_clock_in_date) {
            setFirstClockInDate(response.data.stats.first_clock_in_date);
          } else if (response.data.stats.date_range && response.data.stats.date_range.start) {
            setFirstClockInDate(response.data.stats.date_range.start);
          }
        }
      }
    } catch (error) {
      showToast(t('failed_to_fetch_clock_records'), true);
      shopify.toast.show('Failed to fetch clock records', {
        isError: true
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Filter clock data based on add_salary_enabled and firstClockInDate
  useEffect(() => {
    if (!Array.isArray(clockData)) {
      setFilteredClockData([]);
      return;
    }

    // If add_salary_enabled is true and we have a first clock-in date, filter the data
    if (addSalaryEnabled && firstClockInDate) {
      const firstClockInDateObj = new Date(firstClockInDate);

      // Filter to only show records from first clock-in date onwards
      const filtered = clockData.filter(record => {
        const recordDate = new Date(record.current_date);
        return recordDate >= firstClockInDateObj;
      });

      setFilteredClockData(filtered);
    } else {
      // Otherwise, show all data
      setFilteredClockData(clockData);
    }
  }, [clockData, addSalaryEnabled, firstClockInDate]);

  // Save edited clock times
  // Save edited clock times
  const handleSaveClockTimes = async () => {
    if (!selectedClock || !editedStartTime || !editedEndTime) {
      showToast(t('please_provide_both_clock_in_and_clock_out_times'), true);
      shopify.toast.show('Please provide both clock in and clock out times', {
        isError: true
      });
      return;
    }

    const total_time_worked = calculateDayTotal(editedStartTime, editedEndTime);

    setIsSaving(true);
    try {
      const response = await axiosClient.post(`/edit_clock_times/${selectedClock.id}`, {
        start_at: editedStartTime,
        end_at: editedEndTime,
        staff_id: id,
        current_date: selectedClock.current_date,
        total_time_worked: total_time_worked
      });

      if (response.data.success) {
        showToast(t('clock_times_saved'));
        shopify.toast.show('Clock times saved', {
          style: 'success'
        });
        setIsEditModalOpen(false);
        // Refresh the clock data
        fetchClockDetails();
      } else {
        showToast(response.data.message || 'Failed to update clock times', true);
        shopify.toast.show(response.data.message || 'Failed to update clock times', {
          isError: true
        });
      }
    } catch (error) {
      showToast(t('error_updating_clock_times'), true);
      shopify.toast.show('Error updating clock times', {
        isError: true
      });
    } finally {
      setIsSaving(false);
    }
  };


  const [commissionSortBy, setCommissionSortBy] = useState('recent');
  const [commissionFilterBy, setCommissionFilterBy] = useState('all');

  // Add this function to fetch commission data
  const fetchCommissionData = async () => {
    try {
      setCommissionData(prev => ({ ...prev, loading: true }));
      const response = await axiosClient.get(`staff_commission/${id}`, {
        params: {
          start_date: dateRange.start.toLocaleDateString("en-US"),
          end_date: dateRange.end.toLocaleDateString("en-US")
        }
      });

      if (response.data.success) {
        // Process orders to handle null values
        const processedOrders = (response.data.commission_orders || []).map(order => ({
          ...order,
          items_count: order.items_count || 0,
          earnings: Array.isArray(order.earnings) ? order.earnings.map(earning => ({
            ...earning,
            commission_type: earning.commission_type || 'Fixed',
            quantity: earning.quantity || 1,
            commission_rate: earning.commission_rate || '0.00',
            commission_earned: earning.commission_earned || '0.00'
          })) : []
        }));

        setCommissionData({
          commission_orders: processedOrders,
          active_programs: response.data.active_programs || [],
          stats: response.data.stats || {
            total_commission: 0,
            total_paid_commission: 0,
            total_unpaid_commission: 0,
            total_orders: 0
          },
          loading: false
        });
      }
    } catch (error) {
      showToast(t('failed_to_fetch_commission_data'), true);
      shopify.toast.show('Failed to fetch commission data', {
        isError: true
      });
      setCommissionData(prev => ({ ...prev, loading: false }));
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      await Promise.all([
        fetchStaffDetails(),
        fetchClockDetails(),
      ]);
    };

    fetchData();
  }, [dateRange]); // Only depend on dateRange changes

  const [paymentStats, setPaymentStats] = useState({
    totalEarnings: 0,
    taxType: null,
    taxDeduction: 0,
    netEarnings: 0,
    totalPaid: 0,
    totalUnpaid: 0,
    totalTransactions: 0,
    recentTransactions: []
  });

  // Update the useEffect to handle the new data
  useEffect(() => {
    if (statsData) {
      setPaymentStats({
        totalEarnings: statsData.total_earnings || 0,
        taxType: statsData.tax_type || null,
        taxDeduction: statsData.tax_deduction || 0,
        // Falls back to gross when the API predates this field, so the tile
        // never renders a blank or zero net for an untaxed staff member.
        netEarnings: statsData.net_earnings ?? statsData.total_earnings ?? 0,
        totalPaid: statsData.total_paid || 0,
        totalUnpaid: statsData.total_unpaid || 0,
        totalTransactions: statsData.total_transactions || 0,
        recentTransactions: statsData.recent_transactions || []
      });
    }
  }, [statsData]);

  // Pagination handler
  const handlePaginationChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Calculate total pages
  const totalPages = Math.ceil((filteredClockData?.length || 0) / itemsPerPage);

  const [isProofModalOpen, setIsProofModalOpen] = useState(false);
  const [selectedProof, setSelectedProof] = useState(null);

  // Helper function to get payment method icon
  const getPaymentMethodIcon = (method) => {
    switch (method.toLowerCase()) {
      case 'paypal':
        return <div style={{ margin: '0px' }}><Icon source={PaymentIcon} color="base" /></div>;
      case 'bank transfer':
        return <div style={{ margin: '0px' }}><Icon source={BankIcon} color="base" /></div>;
      case 'cash':
        return <div style={{ margin: '0px' }}><Icon source={PaymentIcon} color="base" /></div>;
      default:
        return <Icon source={PaymentIcon} color="base" />;
    }
  };

  const [isManualEntryModalOpen, setIsManualEntryModalOpen] = useState(false);
  const [manualEntryDate, setManualEntryDate] = useState(new Date());
  const [manualClockInTime, setManualClockInTime] = useState('09:00:00');
  const [manualClockOutTime, setManualClockOutTime] = useState('17:00:00');
  const [isAddingManualEntry, setIsAddingManualEntry] = useState(false);
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [locations, setLocations] = useState([]);
  const [showDatePicker, setShowDatePicker] = useState(false);
  // Date picker state
  const [{ month, year }, setDate] = useState({
    month: new Date().getMonth(),
    year: new Date().getFullYear()
  });
  const [selectedDate, setSelectedDate] = useState({
    start: new Date(),
    end: new Date(),
  });

  // New Time Entry Modal States
  const [isTimeEntryModalOpen, setIsTimeEntryModalOpen] = useState(false);
  const [timeEntryType, setTimeEntryType] = useState('clock-in'); // 'clock-in', 'break', 'clock-out'
  const [timeEntryTime, setTimeEntryTime] = useState('');
  const [breakEndTime, setBreakEndTime] = useState(''); // New state for break end time
  const [timeEntryDate, setTimeEntryDate] = useState({
    start: new Date(),
    end: new Date(),
  });
  const [isAddingTimeEntry, setIsAddingTimeEntry] = useState(false);
  const [showTimeEntryDatePicker, setShowTimeEntryDatePicker] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [isDeletingEntry, setIsDeletingEntry] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState(null);

  // Add handler for new time entry submission
  const handleTimeEntrySubmit = async () => {
    if (!timeEntryTime) {
      showToast(t('please_provide_time'), true);
      shopify.toast.show('Time required', {
        isError: true
      });
      return;
    }

    // For break entries, validate end time
    if (timeEntryType === 'break' && !breakEndTime) {
      showToast(t('please_provide_break_end_time') || 'Please provide break end time', true);
      shopify.toast.show('Break end time required', {
        isError: true
      });
      return;
    }

    if (!selectedLocationId) {
      showToast(t('please_select_a_location'), true);
      shopify.toast.show('Location Required', {
        isError: true
      });
      return;
    }

    // Format date for API
    const formattedDate = `${timeEntryDate.start.getFullYear()}-${String(timeEntryDate.start.getMonth() + 1).padStart(2, '0')}-${String(timeEntryDate.start.getDate()).padStart(2, '0')}`;

    setIsAddingTimeEntry(true);
    try {
      let response;

      if (editingEntry) {
        // Update existing entry
        const payload = {
          time: timeEntryTime,
          location_id: selectedLocationId,
        };

        // Add end time for break entries
        if (timeEntryType === 'break') {
          payload.end_time = breakEndTime;
        }

        response = await axiosClient.post(`/update_time_entry/${editingEntry.id}`, payload);
      } else {
        // Add new entry
        const payload = {
          staff_id: id,
          current_date: formattedDate,
          event_type: timeEntryType,
          time: timeEntryTime,
          location_id: selectedLocationId,
        };

        // Add end time for break entries
        if (timeEntryType === 'break') {
          payload.end_time = breakEndTime;
        }

        response = await axiosClient.post('/add_time_entry', payload);
      }

      if (response.data.success) {
        showToast(editingEntry ? t('time_entry_updated_successfully') || 'Time entry updated successfully' : t('time_entry_added_successfully') || 'Time entry added successfully');
        setIsTimeEntryModalOpen(false);

        // Reset form
        setTimeEntryTime('');
        setBreakEndTime('');
        setTimeEntryType('clock-in');
        setEditingEntry(null);

        // Refresh the clock data
        await fetchClockDetails();

        // If we came from the view entries modal, reopen it with refreshed data
        if (selectedClock && selectedClock.current_date === formattedDate) {
          await handleViewEntriesClick(selectedClock);
        }
      } else {
        showToast(response.data.message || t('failed_to_add_time_entry'), true);
        shopify.toast.show(response.data.message || 'Failed to add time entry', {
          isError: true
        });
      }
    } catch (error) {
      showToast(error.response?.data?.message || t('error_adding_time_entry'), true);
      shopify.toast.show(error.response?.data?.message || 'Error adding time entry', {
        isError: true
      });
    } finally {
      setIsAddingTimeEntry(false);
    }
  };

  // Handler for editing an entry
  const handleEditEntry = (entry) => {
    setEditingEntry(entry);
    setTimeEntryType(entry.event_type);
    setTimeEntryTime(entry.start_at || entry.end_at || '');

    // Set break end time if it's a break entry
    if (entry.event_type === 'break' && entry.end_at) {
      setBreakEndTime(entry.end_at);
    } else {
      setBreakEndTime('');
    }

    // Parse the date correctly
    const dateParts = entry.current_date ? entry.current_date.split('-') : selectedClock.current_date.split('-');
    const dateObj = new Date(dateParts[0], dateParts[1] - 1, dateParts[2]);
    setTimeEntryDate({
      start: dateObj,
      end: dateObj
    });

    setIsViewEntriesModalOpen(false);
    setIsTimeEntryModalOpen(true);
  };

  // State for delete payroll modal
  const [isDeletePayrollModalOpen, setIsDeletePayrollModalOpen] = useState(false);
  const [payrollToDelete, setPayrollToDelete] = useState(null);
  const [isDeletingPayroll, setIsDeletingPayroll] = useState(false);

  const handleDeletePayroll = (transaction) => {
    setPayrollToDelete(transaction);
    setIsDeletePayrollModalOpen(true);
  };

  const confirmDeletePayroll = async () => {
    if (!payrollToDelete) return;

    setIsDeletingPayroll(true);
    try {
      const response = await axiosClient.delete(`/delete_payroll/${payrollToDelete.transaction_id}`);

      if (response.data.success) {
        showToast(t('payroll_deleted_successfully') || 'Payroll transaction deleted successfully');
        setIsDeletePayrollModalOpen(false);
        setPayrollToDelete(null);
        // Refresh data
        fetchClockDetails();
      } else {
        showToast(response.data.message || t('failed_to_delete_payroll'), true);
      }
    } catch (error) {
      showToast(t('error_deleting_payroll'), true);
    } finally {
      setIsDeletingPayroll(false);
    }
  };

  // Handler for deleting an entry
  const handleDeleteEntry = async (entry) => {
    if (!window.confirm(t('are_you_sure_delete_entry') || 'Are you sure you want to delete this entry?')) {
      return;
    }

    setIsDeletingEntry(true);
    try {
      const response = await axiosClient.delete(`/delete_time_entry/${entry.id}`);

      if (response.data.success) {
        showToast(t('time_entry_deleted_successfully') || 'Time entry deleted successfully');

        // Refresh the clock data
        await fetchClockDetails();

        // Refresh the entries list
        if (selectedClock) {
          await handleViewEntriesClick(selectedClock);
        }
      } else {
        showToast(response.data.message || t('failed_to_delete_entry'), true);
        shopify.toast.show(response.data.message || 'Failed to delete entry', {
          isError: true
        });
      }
    } catch (error) {
      showToast(error.response?.data?.message || t('error_deleting_entry'), true);
      shopify.toast.show(error.response?.data?.message || 'Error deleting entry', {
        isError: true
      });
    } finally {
      setIsDeletingEntry(false);
    }
  };

  // Add this function to handle manual entry submission
  const handleManualEntrySubmit = async () => {
    if (!manualClockInTime) {
      showToast(t('please_provide_clock_in_time'), true);
      shopify.toast.show('Clock-in time required', {
        isError: true
      });
      return;
    }

    if (!selectedLocationId) {
      showToast(t('please_select_a_location'), true);
      shopify.toast.show('Location Required', {
        isError: true
      });
      return;
    }

    // Format date for API
    const formattedDate = `${selectedDate.start.getFullYear()}-${String(selectedDate.start.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.start.getDate()).padStart(2, '0')}`;
    const totalTimeWorked = manualClockOutTime ? calculateDayTotal(manualClockInTime, manualClockOutTime) : '';

    setIsAddingManualEntry(true);
    try {
      const response = await axiosClient.post('/add_manual_clock_entry', {
        staff_id: id,
        current_date: formattedDate,
        start_at: manualClockInTime,
        end_at: manualClockOutTime || null,
        location_id: selectedLocationId,
        total_time_worked: totalTimeWorked,
      });

      if (response.data.success) {
        showToast(t('manual_clock_entry_added_successfully'));
        setIsManualEntryModalOpen(false);

        // Reset manual entry form
        setManualClockInTime('09:00:00');
        setManualClockOutTime('17:00:00');

        // Refresh the clock data
        await fetchClockDetails();

        // If we came from the view entries modal, reopen it with refreshed data
        if (selectedClock && selectedClock.current_date === formattedDate) {
          await handleViewEntriesClick(selectedClock);
        }
      } else {
        showToast(response.data.message || t('failed_to_add_manual_clock_entry'), true);
        shopify.toast.show(response.data.message || 'Failed to add manual clock entry', {
          isError: true
        });
      }
    } catch (error) {
      showToast(error.response?.data?.message || t('error_adding_manual_clock_entry'), true);
      shopify.toast.show(error.response?.data?.message || 'Error adding manual clock entry', {
        isError: true
      });
    } finally {
      setIsAddingManualEntry(false);
    }
  };

  const [isLoadingLocations, setIsLoadingLocations] = useState(true);

  // Replace your existing useEffect for fetching locations with this:
  useEffect(() => {
    // Fetch locations 
    const fetchLocations = async () => {
      setIsLoadingLocations(true);
      try {
        const response = await axiosClient.get('/locations');
        if (response.data.success && Array.isArray(response.data.locations)) {
          const locationsList = response.data.locations || [];
          setLocations(locationsList);

          // If locations are loaded but no location is selected yet, select the first one
          if (locationsList.length > 0 && !selectedLocationId) {
            setSelectedLocationId(locationsList[0].id.toString());
          }
        }
      } catch (error) {
        // Error handled silently
      } finally {
        setIsLoadingLocations(false);
      }
    };

    fetchLocations();
  }, []);

  const handleExport = async () => {
    try {
      const response = await axiosClient.get(`/clock/${id}/export`, {
        params: {
          start_date: dateRange.start.toLocaleDateString("en-US"),
          end_date: dateRange.end.toLocaleDateString("en-US")
        },
        responseType: 'blob'
      });

      // Create a download link and trigger it
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      // Generate filename
      const fileName = `Clock_Data_${staffData?.first_name || ''}_${staffData?.last_name || ''}_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.setAttribute('download', fileName);

      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showToast(t('export_completed'));
      shopify.toast.show('Export completed');
    } catch (error) {
      showToast(t('failed_to_export_data'), true);
      shopify.toast.show('Failed to export data', {
        isError: true
      });
    }
  };

  return (
    <>
      <Page
        fullWidth
        backAction={{
          content: t('staff'),
          url: "#",
          onAction: () => navigate("/StaffManagement"),
        }}
        title={`${staffData?.first_name} ${staffData?.last_name}`}
        titleMetadata={
          <Badge tone={staffData?.status === 'Active' ? 'success' : 'warning'}>
            {staffData?.status}
          </Badge>
        }
        secondaryActions={
          <DateRangePicker
            dateRange={dateRange}
            setDateRange={setDateRange}
          />
        }
      >
        <div className="mb-4">
          <SegmentedTabs tabs={tabs} selected={selectedTab} onSelect={handleTabChange} />
        </div>
        <div>
          {selectedTab === 0 && (
            <Layout>
              <Layout.Section>
                <Card>
                  <div className="p-3 md:p-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6">
                      {/* First Row */}
                      {/* Total Earnings */}
                      <div className="bg-white p-3 md:p-4 rounded-lg border border-[#e1e3e5] hover:border-[#2c6ecb] transition-all duration-200 shadow-sm hover:shadow-md">
                        <div className="flex items-start md:items-center gap-3">
                          <div className="p-2 rounded-full bg-[#f1f8ff] shrink-0">
                            <Icon
                              source={WalletIcon}
                              color="primary"
                              tone="strong"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[#6d7175] text-xs md:text-sm">{t('total_earnings')}</p>
                            <Tooltip content={`${currencyData[staffData?.currency]?.symbol || ''}${Number(paymentStats.totalEarnings || 0).toFixed(2)}`}>
                              <p className="text-lg md:text-xl font-semibold text-[#202223] mt-1 truncate">
                                {currencyData[staffData?.currency]?.symbol || ''}{Number(paymentStats.totalEarnings || 0).toFixed(2)}
                              </p>
                            </Tooltip>
                            {paymentStats.taxType ? (
                              <div className="mt-2 pt-2 border-t border-[#e1e3e5]">
                                <p className="text-[#6d7175] text-xs">
                                  {t('tax_deduction')}: -{currencyData[staffData?.currency]?.symbol || ''}{Number(paymentStats.taxDeduction || 0).toFixed(2)}
                                </p>
                                <p className="text-[#202223] text-sm font-semibold mt-1">
                                  {t('net_earnings')}: {currencyData[staffData?.currency]?.symbol || ''}{Number(paymentStats.netEarnings || 0).toFixed(2)}
                                </p>
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </div>

                      {/* Total Hours */}
                      <div className="bg-white p-3 md:p-4 rounded-lg border border-[#e1e3e5] hover:border-[#2c6ecb] transition-all duration-200 shadow-sm hover:shadow-md">
                        <div className="flex items-start md:items-center gap-3">
                          <div className="p-2 rounded-full bg-[#f1f8ff] shrink-0">
                            <Icon
                              source={ClockIcon}
                              color="primary"
                              tone="strong"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[#6d7175] text-xs md:text-sm">{t('total_hours')}</p>
                            <Tooltip content={statsData.total_hours}>
                              <p className="text-lg md:text-xl font-semibold text-[#202223] mt-1 truncate">
                                {statsData.total_hours}
                              </p>
                            </Tooltip>
                          </div>
                        </div>
                      </div>

                      {/* Working Hours */}
                      <div className="bg-white p-3 md:p-4 rounded-lg border border-[#e1e3e5] hover:border-[#2c6ecb] transition-all duration-200 shadow-sm hover:shadow-md">
                        <div className="flex items-start md:items-center gap-3">
                          <div className="p-2 rounded-full bg-[#f1f8ff] shrink-0">
                            <Icon
                              source={ClockIcon}
                              color="primary"
                              tone="strong"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[#6d7175] text-xs md:text-sm">{t('working_hours')}</p>
                            <Tooltip content={statsData.working_hours}>
                              <p className="text-lg md:text-xl font-semibold text-[#202223] mt-1 truncate">
                                {statsData.working_hours}
                              </p>
                            </Tooltip>
                          </div>
                        </div>
                      </div>

                      {/* Total Break Time */}
                      <div className="bg-white p-3 md:p-4 rounded-lg border border-[#e1e3e5] hover:border-[#2c6ecb] transition-all duration-200 shadow-sm hover:shadow-md">
                        <div className="flex items-start md:items-center gap-3">
                          <div className="p-2 rounded-full bg-yellow-100 shrink-0">
                            <Icon
                              source={ClockIcon}
                              color="primary"
                              tone="strong"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[#6d7175] text-xs md:text-sm">{t('total_break_time')}</p>
                            <Tooltip content={statsData.total_break_time}>
                              <p className="text-lg md:text-xl font-semibold text-[#202223] mt-1 truncate">
                                {statsData.total_break_time}
                              </p>
                            </Tooltip>
                          </div>
                        </div>
                      </div>



                      {/* Similar pattern for other cards */}
                      {/* Second Row */}
                      {/* Paid */}
                      <div className="bg-white p-3 md:p-4 rounded-lg border border-[#e1e3e5] hover:border-[#2c6ecb] transition-all duration-200 shadow-sm hover:shadow-md">
                        <div className="flex items-start md:items-center gap-3">
                          <div className="p-2 rounded-full bg-[#ebf9eb] shrink-0">
                            <Icon
                              source={CheckSmallIcon}
                              color="success"
                              tone="strong"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[#6d7175] text-xs md:text-sm">{t('paid')}</p>
                            <Tooltip content={`${currencyData[staffData?.currency]?.symbol || ''}${paymentStats.totalPaid.toFixed(2)}`}>
                              <p className="text-lg md:text-xl font-semibold text-[#202223] mt-1 truncate">
                                {currencyData[staffData?.currency]?.symbol || ''}{paymentStats.totalPaid.toFixed(2)}
                              </p>
                            </Tooltip>
                          </div>
                        </div>
                      </div>

                      {/* Unpaid */}
                      <div className="bg-white p-3 md:p-4 rounded-lg border border-[#e1e3e5] hover:border-[#2c6ecb] transition-all duration-200 shadow-sm hover:shadow-md">
                        <div className="flex items-start md:items-center gap-3">
                          <div className="p-2 rounded-full bg-yellow-100 shrink-0">
                            <Icon
                              source={AlertBubbleIcon}
                              color="critical"
                              tone="strong"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[#6d7175] text-xs md:text-sm">{t('unpaid')}</p>
                            <Tooltip content={`${currencyData[staffData?.currency]?.symbol || ''}${Number(paymentStats.totalUnpaid || 0).toFixed(2)}`}>
                              <p className="text-lg md:text-xl font-semibold text-[#202223] mt-1 truncate">
                                {currencyData[staffData?.currency]?.symbol || ''}{Number(paymentStats.totalUnpaid || 0).toFixed(2)}
                              </p>
                            </Tooltip>
                          </div>
                        </div>
                      </div>

                      {/* Third Row */}
                      {/* Total Absents */}
                      <div className="bg-white p-3 md:p-4 rounded-lg border border-[#e1e3e5] hover:border-[#2c6ecb] transition-all duration-200 shadow-sm hover:shadow-md">
                        <div className="flex items-start md:items-center gap-3">
                          <div className="p-2 rounded-full bg-yellow-100 shrink-0">
                            <Icon
                              source={MinusIcon}
                              color="critical"
                              tone="strong"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[#6d7175] text-xs md:text-sm">{t('total_absents')}</p>
                            <Tooltip content={statsData.total_absents}>
                              <p className="text-lg md:text-xl font-semibold text-[#202223] mt-1 truncate">
                                {statsData.total_absents} days
                              </p>
                            </Tooltip>
                          </div>
                        </div>
                      </div>

                      {/* Unpaid Salary */}
                      <div className="bg-white p-3 md:p-4 rounded-lg border border-[#e1e3e5] hover:border-[#2c6ecb] transition-all duration-200 shadow-sm hover:shadow-md">
                        <div className="flex items-start md:items-center gap-3">
                          <div className="p-2 rounded-full bg-[#ebf9eb] shrink-0">
                            <Icon
                              source={MoneyNoneIcon}
                              color="success"
                              tone="strong"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[#6d7175] text-xs md:text-sm">{t('unpaid_salary')}</p>
                            <Tooltip content={`${currencyData[staffData?.currency]?.symbol || ''}${statsData.unpaid_salary}`}>
                              <p className="text-lg md:text-xl font-semibold text-[#202223] mt-1 truncate">
                                {currencyData[staffData?.currency]?.symbol || ''}{statsData.unpaid_salary}
                              </p>
                            </Tooltip>
                          </div>
                        </div>
                      </div>

                      {/* Total Transactions */}
                      <div className="bg-white p-3 md:p-4 rounded-lg border border-[#e1e3e5] hover:border-[#2c6ecb] transition-all duration-200 shadow-sm hover:shadow-md">
                        <div className="flex items-start md:items-center gap-3">
                          <div className="p-2 rounded-full bg-[#f1f8ff] shrink-0">
                            <Icon
                              source={TransactionIcon}
                              color="primary"
                              tone="strong"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[#6d7175] text-xs md:text-sm">{t('total_transactions')}</p>
                            <Tooltip content={paymentStats.totalTransactions}>
                              <p className="text-lg md:text-xl font-semibold text-[#202223] mt-1 truncate">
                                {paymentStats.totalTransactions}
                              </p>
                            </Tooltip>
                          </div>
                        </div>
                      </div>

                      {/* Total Bonus */}
                      <div className="bg-white p-3 md:p-4 rounded-lg border border-[#e1e3e5] hover:border-[#2c6ecb] transition-all duration-200 shadow-sm hover:shadow-md">
                        <div className="flex items-start md:items-center gap-3">
                          <div className="p-2 rounded-full bg-[#ebf9eb] shrink-0">
                            <Icon
                              source={RewardIcon}
                              color="primary"
                              tone="strong"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[#6d7175] text-xs md:text-sm">{t('total_bonus')}</p>
                            <Tooltip content={`${currencyData[staffData?.currency]?.symbol || ''}${statsData.totalBonus}`}>
                              <p className="text-lg md:text-xl font-semibold text-[#202223] mt-1 truncate">
                                {currencyData[staffData?.currency]?.symbol || ''}{statsData.totalBonus}
                              </p>
                            </Tooltip>
                          </div>
                        </div>
                      </div>

                      {/* Total Leaves - NEW CARD */}
                      <div className="bg-white p-3 md:p-4 rounded-lg border border-[#e1e3e5] hover:border-[#2c6ecb] transition-all duration-200 shadow-sm hover:shadow-md">
                        <div className="flex items-start md:items-center gap-3">
                          <div className="p-2 rounded-full bg-[#f1f8ff] shrink-0">
                            <Icon
                              source={CalendarIcon}
                              color="primary"
                              tone="strong"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[#6d7175] text-xs md:text-sm">{t('total_leaves')}</p>
                            <Tooltip content={`${statsData.total_leaves || 0} days`}>
                              <p className="text-lg md:text-xl font-semibold text-[#202223] mt-1 truncate">
                                {statsData.total_leaves || 0} days
                              </p>
                            </Tooltip>
                          </div>
                        </div>
                      </div>

                      {/* Paid Leaves - NEW CARD */}
                      <div className="bg-white p-3 md:p-4 rounded-lg border border-[#e1e3e5] hover:border-[#2c6ecb] transition-all duration-200 shadow-sm hover:shadow-md">
                        <div className="flex items-start md:items-center gap-3">
                          <div className="p-2 rounded-full bg-[#ebf9eb] shrink-0">
                            <Icon
                              source={CheckSmallIcon}
                              color="success"
                              tone="strong"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[#6d7175] text-xs md:text-sm">{t('paid_leaves')}</p>
                            <Tooltip content={`${statsData.paid_leaves || 0} days`}>
                              <p className="text-lg md:text-xl font-semibold text-[#202223] mt-1 truncate">
                                {statsData.paid_leaves || 0} days
                              </p>
                            </Tooltip>
                          </div>
                        </div>
                      </div>

                      {/* UnPaid Leaves - NEW CARD */}
                      <div className="bg-white p-3 md:p-4 rounded-lg border border-[#e1e3e5] hover:border-[#2c6ecb] transition-all duration-200 shadow-sm hover:shadow-md">
                        <div className="flex items-start md:items-center gap-3">
                          <div className="p-2 rounded-full bg-yellow-100 shrink-0">
                            <Icon
                              source={CheckSmallIcon}
                              color="success"
                              tone="strong"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[#6d7175] text-xs md:text-sm">{t('unpaid_leaves')}</p>
                            <Tooltip content={`${statsData.unpaid_leaves || 0} days`}>
                              <p className="text-lg md:text-xl font-semibold text-[#202223] mt-1 truncate">
                                {statsData.unpaid_leaves || 0} days
                              </p>
                            </Tooltip>
                          </div>
                        </div>
                      </div>

                    </div>
                  </div>
                </Card>
              </Layout.Section>
              <Layout.Section variant="oneThird">
                <Card sectioned>
                  <>
                    <div className="flex items-center justify-end ml-auto " style={{ float: "right" }}>
                      <Button
                        icon={EditIcon}
                        variant="plain"
                        onClick={() => navigate(`/StaffManagement/EditStaff/${staffData?.id}`)}
                      >
                        Edit
                      </Button>
                    </div>
                    <div >
                      <p className="text-md p-2">
                        <b>{t('name')}</b> : {staffData?.first_name} {staffData?.last_name}
                      </p>
                      <p className="text-md my-3 p-2">
                        <b>{t('email')}</b> : {staffData?.email}
                      </p>
                      <p className="text-md my-3 p-2">
                        <b>{t('phone')}</b> : {staffData?.phone}
                      </p>
                      <p className="text-md my-3 p-2">
                        <b>{t('position')}</b> : {staffData?.position}
                      </p>
                      <p className="text-md my-3 p-2">
                        <b>{t('payment_method')}</b> : {staffData?.payment_method}
                      </p>
                      {staffData?.payment_method === 'PayPal' && (
                        <div className="bg-gray-50 p-2 rounded my-2 border border-gray-100" style={{ padding: '8px', backgroundColor: '#F9FAFB', border: '1px solid #F3F4F6', borderRadius: '6px', margin: '8px 0' }}>
                          <p className="text-sm my-1" style={{ fontSize: '13px', margin: '4px 0' }}>
                            <b>PayPal Name</b>: {staffData?.paypal_name || 'N/A'}
                          </p>
                          <p className="text-sm my-1" style={{ fontSize: '13px', margin: '4px 0' }}>
                            <b>PayPal Email</b>: {staffData?.payment_email || 'N/A'}
                          </p>
                        </div>
                      )}
                      {staffData?.payment_method === 'Stripe' && (
                        <div className="bg-gray-50 p-2 rounded my-2 border border-gray-100" style={{ padding: '8px', backgroundColor: '#F9FAFB', border: '1px solid #F3F4F6', borderRadius: '6px', margin: '8px 0' }}>
                          <p className="text-sm my-1" style={{ fontSize: '13px', margin: '4px 0' }}>
                            <b>Stripe Email</b>: {staffData?.stripe_email || 'N/A'}
                          </p>
                          <p className="text-sm my-1" style={{ fontSize: '13px', margin: '4px 0' }}>
                            <b>Stripe Account ID</b>: {staffData?.stripe_account_id || 'N/A'}
                          </p>
                        </div>
                      )}
                      {staffData?.payment_method === 'Wise' && (
                        <div className="bg-gray-50 p-2 rounded my-2 border border-gray-100" style={{ padding: '8px', backgroundColor: '#F9FAFB', border: '1px solid #F3F4F6', borderRadius: '6px', margin: '8px 0' }}>
                          <p className="text-sm my-1" style={{ fontSize: '13px', margin: '4px 0' }}>
                            <b>Wise Account Name</b>: {staffData?.wise_account_name || 'N/A'}
                          </p>
                          <p className="text-sm my-1" style={{ fontSize: '13px', margin: '4px 0' }}>
                            <b>Wise Email</b>: {staffData?.wise_email || 'N/A'}
                          </p>
                          <p className="text-sm my-1" style={{ fontSize: '13px', margin: '4px 0' }}>
                            <b>Currency</b>: {staffData?.wise_currency || 'N/A'}
                          </p>
                        </div>
                      )}
                      {staffData?.payment_method === 'Payoneer' && (
                        <div className="bg-gray-50 p-2 rounded my-2 border border-gray-100" style={{ padding: '8px', backgroundColor: '#F9FAFB', border: '1px solid #F3F4F6', borderRadius: '6px', margin: '8px 0' }}>
                          <p className="text-sm my-1" style={{ fontSize: '13px', margin: '4px 0' }}>
                            <b>Payoneer Name</b>: {staffData?.payoneer_name || 'N/A'}
                          </p>
                          <p className="text-sm my-1" style={{ fontSize: '13px', margin: '4px 0' }}>
                            <b>Payoneer Email</b>: {staffData?.payoneer_email || 'N/A'}
                          </p>
                        </div>
                      )}
                      {(staffData?.payment_method === 'Bank Transfer' || staffData?.payment_method === 'Direct Deposit') && (
                        <div className="bg-gray-50 p-2 rounded my-2 border border-gray-100" style={{ padding: '8px', backgroundColor: '#F9FAFB', border: '1px solid #F3F4F6', borderRadius: '6px', margin: '8px 0' }}>
                          <p className="text-sm my-1" style={{ fontSize: '13px', margin: '4px 0' }}>
                            <b>{t('bank_account_type') || 'Account Type'}</b>: {staffData?.bank_account_type === 'domestic' ? (t('domestic') || 'Domestic') : (t('international_swift') || 'International')}
                          </p>
                          <p className="text-sm my-1" style={{ fontSize: '13px', margin: '4px 0' }}>
                            <b>{t('bank_name') || 'Bank Name'}</b>: {staffData?.bank_name || 'N/A'}
                          </p>
                          <p className="text-sm my-1" style={{ fontSize: '13px', margin: '4px 0' }}>
                            <b>{t('account_holder_name') || 'Account Holder'}</b>: {staffData?.account_holder_name || 'N/A'}
                          </p>
                          <p className="text-sm my-1" style={{ fontSize: '13px', margin: '4px 0' }}>
                            <b>{t('account_number') || 'Account Number'}</b>: {staffData?.account_number || 'N/A'}
                          </p>
                          {staffData?.bank_account_type === 'domestic' ? (
                            <p className="text-sm my-1" style={{ fontSize: '13px', margin: '4px 0' }}>
                              <b>{t('routing_number') || 'Routing Number'}</b>: {staffData?.routing_number || 'N/A'}
                            </p>
                          ) : (
                            <>
                              <p className="text-sm my-1" style={{ fontSize: '13px', margin: '4px 0' }}>
                                <b>SWIFT/BIC Code</b>: {staffData?.swift_code || 'N/A'}
                              </p>
                              <p className="text-sm my-1" style={{ fontSize: '13px', margin: '4px 0' }}>
                                <b>IBAN</b>: {staffData?.iban || 'N/A'}
                              </p>
                            </>
                          )}
                        </div>
                      )}
                      {staffData?.payment_method === 'Revolut' && (
                        <div className="bg-gray-50 p-2 rounded my-2 border border-gray-100" style={{ padding: '8px', backgroundColor: '#F9FAFB', border: '1px solid #F3F4F6', borderRadius: '6px', margin: '8px 0' }}>
                          <p className="text-sm my-1" style={{ fontSize: '13px', margin: '4px 0' }}>
                            <b>Revolut Username</b>: {staffData?.revolut_username || 'N/A'}
                          </p>
                          <p className="text-sm my-1" style={{ fontSize: '13px', margin: '4px 0' }}>
                            <b>Revolut Contact</b>: {staffData?.revolut_contact || 'N/A'}
                          </p>
                        </div>
                      )}
                      {staffData?.payment_method === 'Venmo' && (
                        <div className="bg-gray-50 p-2 rounded my-2 border border-gray-100" style={{ padding: '8px', backgroundColor: '#F9FAFB', border: '1px solid #F3F4F6', borderRadius: '6px', margin: '8px 0' }}>
                          <p className="text-sm my-1" style={{ fontSize: '13px', margin: '4px 0' }}>
                            <b>Venmo Username</b>: {staffData?.venmo_username || 'N/A'}
                          </p>
                          <p className="text-sm my-1" style={{ fontSize: '13px', margin: '4px 0' }}>
                            <b>Venmo Phone</b>: {staffData?.venmo_phone || 'N/A'}
                          </p>
                        </div>
                      )}
                      {staffData?.payment_method === 'Square' && (
                        <div className="bg-gray-50 p-2 rounded my-2 border border-gray-100" style={{ padding: '8px', backgroundColor: '#F9FAFB', border: '1px solid #F3F4F6', borderRadius: '6px', margin: '8px 0' }}>
                          <p className="text-sm my-1" style={{ fontSize: '13px', margin: '4px 0' }}>
                            <b>CashTag</b>: {staffData?.cash_tag || 'N/A'}
                          </p>
                          <p className="text-sm my-1" style={{ fontSize: '13px', margin: '4px 0' }}>
                            <b>Square Contact</b>: {staffData?.cash_app_contact || 'N/A'}
                          </p>
                        </div>
                      )}
                      <p className="text-md my-3 p-2">
                        <b>{t('payroll_type')}</b> : {staffData?.payroll_type}
                      </p>
                      <p className="text-md my-3 p-2">
                        <b>{t('location')}</b> : {staffData?.locations}
                      </p>
                    </div>
                  </>
                </Card>
              </Layout.Section>
              <Layout.Section>
                <Card title={t('attendance_records')} sectioned>
                  <div className="p-4 flex justify-between">
                    <Button
                      variant="plain"
                      onClick={() => navigate("/Settings")}
                      className="flex items-center"
                    >
                      <Icon source={SettingsIcon} color="base" />
                      <span className="ml-1">{t('adjust_holiday_leave_settings')}</span>
                    </Button>
                    <div className="flex gap-2">
                      <Button
                        icon={ExportIcon}
                        onClick={handleExport}
                      >
                        {t('export')}
                      </Button>
                      <Button
                        variant="primary"
                        icon={PlusIcon}
                        onClick={() => setIsManualEntryModalOpen(true)}
                      >
                        {t('add_manual_entry')}
                      </Button>
                    </div>
                  </div>
                  {isLoading ? (
                    <div className="p-4 text-center">
                      <Spinner accessibilityLabel="Loading" size="large" />
                    </div>
                  ) : (
                    <>
                      <div className="overflow-x-auto">
                        <DataTable
                          columnContentTypes={[
                            'text',
                            'text',
                            'text',
                            'text',
                            'text',
                            'text',
                            'text',
                            'text',
                          ]}
                          headings={[
                            <ColumnHeading icon={CalendarIcon}>{t('date')}</ColumnHeading>,
                            <ColumnHeading icon={StatusActiveIcon}>{t('status')}</ColumnHeading>,
                            <ColumnHeading icon={LocationIcon}>{t('location')}</ColumnHeading>,
                            <ColumnHeading icon={ClockIcon}>{t('break_time')}</ColumnHeading>,
                            <ColumnHeading icon={ClockIcon}>{t('first_in')}</ColumnHeading>,
                            <ColumnHeading icon={ClockIcon}>{t('last_out')}</ColumnHeading>,
                            <ColumnHeading icon={ClockIcon}>{t('total_hours')}</ColumnHeading>,
                            <ColumnHeading icon={SettingsIcon}>{t('action')}</ColumnHeading>
                          ]}
                          rows={getClockRows()}
                          footerContent={
                            totalPages > 1 ? (
                              <div className="flex justify-center py-4">
                                <Pagination
                                  hasPrevious={currentPage > 1}
                                  onPrevious={() => handlePaginationChange(currentPage - 1)}
                                  hasNext={currentPage < totalPages}
                                  onNext={() => handlePaginationChange(currentPage + 1)}
                                />
                                <div className="ml-4 text-sm text-gray-500">
                                  {t('page')} {currentPage} {t('of')} {totalPages}
                                </div>
                              </div>
                            ) : null
                          }
                        />
                      </div>
                    </>
                  )}
                </Card>
              </Layout.Section>
            </Layout>
          )}
          {false && ( /* Commission Program removed for universal */
            <Layout>
              <Layout.Section>
                <Card>
                  <div className="p-5 md:p-6">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                      {/* Total Commission */}
                      <div className="bg-white p-5 rounded-lg border border-[#e1e3e5] hover:border-[#2c6ecb] transition-all duration-300 shadow-sm hover:shadow-lg">
                        <div className="flex items-center gap-4">
                          <div className="p-3 rounded-full bg-[#f1f8ff] shrink-0">
                            <Icon source={DiscountIcon} color="primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[#6d7175] text-sm font-medium">{t('total_commission')}</p>
                            <Tooltip content={formatCurrency(commissionData.stats.total_commission)}>
                              <p className="text-2xl font-bold text-[#202223] mt-2 truncate">
                                {formatCurrency(commissionData.stats.total_commission)}
                              </p>
                            </Tooltip>
                          </div>
                        </div>
                      </div>

                      {/* Paid Commission - with similar styling improvements */}
                      <div className="bg-white p-5 rounded-lg border border-[#e1e3e5] hover:border-[#2c6ecb] transition-all duration-300 shadow-sm hover:shadow-lg">
                        <div className="flex items-center gap-4">
                          <div className="p-3 rounded-full bg-[#ebf9eb] shrink-0">
                            <Icon source={CheckSmallIcon} color="success" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[#6d7175] text-sm font-medium">{t('paid_commission')}</p>
                            <Tooltip content={formatCurrency(commissionData.stats.total_paid_commission)}>
                              <p className="text-2xl font-bold text-[#202223] mt-2 truncate">
                                {formatCurrency(commissionData.stats.total_paid_commission)}
                              </p>
                            </Tooltip>
                          </div>
                        </div>
                      </div>

                      {/* Unpaid Commission - with similar styling improvements */}
                      <div className="bg-white p-5 rounded-lg border border-[#e1e3e5] hover:border-[#2c6ecb] transition-all duration-300 shadow-sm hover:shadow-lg">
                        <div className="flex items-center gap-4">
                          <div className="p-3 rounded-full bg-yellow-100 shrink-0">
                            <Icon source={AlertBubbleIcon} color="critical" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[#6d7175] text-sm font-medium">{t('unpaid_commission')}</p>
                            <Tooltip content={formatCurrency(commissionData.stats.total_unpaid_commission)}>
                              <p className="text-2xl font-bold text-[#202223] mt-2 truncate">
                                {formatCurrency(commissionData.stats.total_unpaid_commission)}
                              </p>
                            </Tooltip>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              </Layout.Section>

              {/* Commission Orders */}
              <Layout.Section>
                <Card>
                  <div className="p-5 border-b border-[#e1e3e5]">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                      <div className="flex items-center gap-2">
                        <div className="p-2 rounded-full bg-[#f1f8ff]">
                          <Icon source={OrderIcon} color="primary" />
                        </div>
                        <Text variant="headingMd" as="h2">
                          {selectedProgram ? `${t('orders_for')} ${selectedProgram.program_name}` : t('commission_orders')}
                        </Text>
                      </div>
                    </div>
                  </div>

                  {/* Enhanced Filter Section with Program Selection */}
                  <div className="p-4 bg-[#f9fafb] border-b border-[#e1e3e5]">
                    <div className="flex flex-wrap gap-3">
                      {/* Program Selection Dropdown */}
                      <div className="flex-1 min-w-[200px]">
                        <Select
                          label={t('commission_programs')}
                          labelInline
                          options={[
                            { label: t('all_programs'), value: 'all' },
                            ...(commissionData.active_programs?.map(program => ({
                              label: program.program_name,
                              value: program.id.toString()
                            })) || [])
                          ]}
                          value={selectedProgramId}
                          onChange={(value) => {
                            setSelectedProgramId(value);

                            // If value is not 'all', find the program object and set it
                            if (value !== 'all') {
                              const program = commissionData.active_programs?.find(p => p.id.toString() === value);
                              setSelectedProgram(program || null);
                            } else {
                              setSelectedProgram(null);
                            }
                          }}
                        />
                      </div>

                      {/* Status Filter */}
                      <div className="flex-1 min-w-[180px]">
                        <Select
                          label={t('status')}
                          labelInline
                          options={[
                            { label: t('all_orders'), value: 'all' },
                            { label: t('paid'), value: 'paid' },
                            { label: t('unpaid'), value: 'unpaid' }
                          ]}
                          value={commissionFilterBy}
                          onChange={setCommissionFilterBy}
                        />
                      </div>

                      {/* Sort Option */}
                      <div className="flex-1 min-w-[180px]">
                        <Select
                          label={t('sort')}
                          labelInline
                          options={[
                            { label: t('most_recent'), value: 'recent' },
                            { label: t('oldest_first'), value: 'oldest' },
                            { label: t('highest_amount'), value: 'highest' },
                            { label: t('lowest_amount'), value: 'lowest' },
                          ]}
                          value={commissionSortBy}
                          onChange={setCommissionSortBy}
                        />
                      </div>
                    </div>

                    {/* Selected Program Information with enhanced styling */}
                    {selectedProgram && (
                      <div className="mt-4 p-3 bg-white rounded-lg border border-[#e1e3e5] shadow-sm">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-[#f1f8ff] flex items-center justify-center">
                              <Text variant="headingLg" as="p" fontWeight="bold" color="primary">
                                {selectedProgram.program_name.substring(0, 2).toUpperCase()}
                              </Text>
                            </div>
                            <div>
                              <Text variant="bodyMd" fontWeight="semibold">
                                {t('showing_orders_for')}: {selectedProgram.program_name}
                              </Text>
                              <div className="flex items-center gap-2 mt-1">
                                <Badge tone="info">
                                  {selectedProgram.commission_type === 'Percentage' ? t('percentage') : t('fixed')}
                                </Badge>
                                <Text variant="bodySm" color="subdued">
                                  {selectedProgram.commission_type === 'Percentage' ?
                                    `${selectedProgram.all_products_commission}%` :
                                    formatCurrency(selectedProgram.all_products_commission)}
                                  {' commission'}
                                </Text>
                              </div>
                            </div>
                          </div>
                          <Button
                            plain
                            icon={XIcon}
                            onClick={() => {
                              setSelectedProgram(null);
                              setSelectedProgramId('all');
                            }}
                            accessibilityLabel="Clear program filter"
                          >
                            {t('clear_filter')}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>

                  {commissionData.loading ? (
                    <div className="p-12 text-center">
                      <Spinner accessibilityLabel="Loading commission orders" size="large" />
                      <p className="mt-4 text-gray-500">
                        {selectedProgram
                          ? `${t('loading_orders_for')} ${selectedProgram.program_name}...`
                          : `${t('loading_commission_orders')}...`}
                      </p>
                    </div>
                  ) : (
                    <>
                      {/* Showing count of filtered orders */}
                      <div className="px-4 py-2 border-b border-[#e1e3e5] bg-white">
                        <Text variant="bodySm" color="subdued">
                          {(() => {
                            // Calculate filtered orders count
                            const filteredOrders = commissionData.commission_orders
                              .filter(order => {
                                // First filter by program if one is selected
                                if (selectedProgram) {
                                  const hasSelectedProgram = order.earnings?.some(
                                    earning => earning.program_id.toString() === selectedProgram.id.toString()
                                  );
                                  if (!hasSelectedProgram) {
                                    return false;
                                  }
                                }

                                // Then filter by payment status
                                if (commissionFilterBy === 'all') return true;
                                if (commissionFilterBy === 'paid') return order.earnings[0].payroll_id != null;
                                if (commissionFilterBy === 'unpaid') return order.earnings[0].payroll_id == null;

                                return true;
                              });

                            return `Showing ${filteredOrders.length} ${filteredOrders.length === 1 ? 'order' : 'orders'}${selectedProgram ? ` for ${selectedProgram.program_name}` : ''
                              }`;
                          })()}
                        </Text>
                      </div>

                      {/* Filter the orders */}
                      {(() => {
                        // Get filtered and sorted orders
                        const filteredOrders = commissionData.commission_orders
                          .filter(order => {
                            // First filter by program if one is selected
                            if (selectedProgram) {
                              const hasSelectedProgram = order.earnings?.some(
                                earning => earning.program_id.toString() === selectedProgram.id.toString()
                              );
                              if (!hasSelectedProgram) {
                                return false;
                              }
                            }

                            // Then filter by status
                            if (commissionFilterBy === 'all') return true;
                            if (commissionFilterBy === 'paid') return order.earnings[0].payroll_id != null;
                            if (commissionFilterBy === 'unpaid') return order.earnings[0].payroll_id == null;

                            return true;
                          })
                          .sort((a, b) => {
                            switch (commissionSortBy) {
                              case 'oldest':
                                return new Date(a.earned_at) - new Date(b.earned_at);
                              case 'highest':
                                return parseFloat(b.total_commission) - parseFloat(a.total_commission);
                              case 'lowest':
                                return parseFloat(a.total_commission) - parseFloat(b.total_commission);
                              case 'recent':
                              default:
                                return new Date(b.earned_at) - new Date(a.earned_at);
                            }
                          });

                        // Calculate total pages and paginate
                        const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);
                        const indexOfLastItem = currentPage * itemsPerPage;
                        const indexOfFirstItem = indexOfLastItem - itemsPerPage;
                        const currentOrders = filteredOrders.slice(indexOfFirstItem, indexOfLastItem);

                        return filteredOrders.length > 0 ? (
                          <>
                            <DataTable
                              columnContentTypes={["text", "text", "text", "text", "text"]}
                              headings={[t('date'), t('order'), t('commission'), t('status'), t('actions')]}
                              rows={currentOrders.map(order => [
                                // Date with enhanced formatting
                                <div className="flex items-center gap-1">
                                  <div className="p-1 rounded-full bg-[#f9fafb]">
                                    <Icon source={CalendarIcon} color="base" />
                                  </div>
                                  <Text variant="bodyMd">
                                    {new Date(order.earned_at).toLocaleDateString('en-US', {
                                      month: 'short',
                                      day: 'numeric',
                                      year: 'numeric'
                                    })}
                                  </Text>
                                </div>,

                                // Order Info with enhanced styling
                                <div className="flex items-center gap-2">
                                  <div className="p-1 rounded bg-[#f1f8ff]">
                                    <Icon source={OrderIcon} color="primary" />
                                  </div>
                                  <div>
                                    <Text variant="bodyMd" fontWeight="bold">#{order.order_id}</Text>
                                    <Text variant="bodySm" color="subdued">{order.items_count} {t('items')}</Text>
                                  </div>
                                </div>,

                                // Commission Amount with program highlight
                                <div>
                                  <Text fontWeight="bold" variant="bodyMd">
                                    {formatCurrency(order.total_commission)}
                                  </Text>
                                  {!selectedProgram && order.program_names && (
                                    <Text variant="bodySm" color="subdued" truncate>
                                      {order.program_names}
                                    </Text>
                                  )}
                                  {selectedProgram && (
                                    <div className="flex items-center gap-1 mt-1">
                                      <div className="w-2 h-2 rounded-full bg-[#008060]"></div>
                                      <Text variant="bodySm" color="success" truncate>
                                        {selectedProgram.program_name}
                                      </Text>
                                    </div>
                                  )}
                                </div>,

                                // Status with clear visual indicator
                                <Badge
                                  tone={order.earnings[0].payroll_id != null ? 'success' : 'critical'}
                                  progress={order.earnings[0].payroll_id != null ? 'complete' : 'incomplete'}
                                >
                                  {order.earnings[0].payroll_id != null ? t('paid') : t('unpaid')}
                                </Badge>,

                                // Actions with enhanced tooltips
                                <Tooltip content="View detailed commission breakdown">
                                  <Button
                                    size="slim"
                                    icon={ViewIcon}
                                    onClick={() => {
                                      setSelectedOrderDetails(order);
                                    }}
                                  >
                                    {t('view_details')}
                                  </Button>
                                </Tooltip>
                              ])}
                            />

                            {/* Pagination controls */}
                            {filteredOrders.length > itemsPerPage && (
                              <div className="flex items-center justify-center p-4 border-t border-[#e1e3e5]">
                                <Pagination
                                  hasPrevious={currentPage > 1}
                                  onPrevious={() => setCurrentPage(currentPage - 1)}
                                  hasNext={currentPage < totalPages}
                                  onNext={() => setCurrentPage(currentPage + 1)}
                                  label={`${indexOfFirstItem + 1}-${Math.min(indexOfLastItem, filteredOrders.length)} of ${filteredOrders.length}`}
                                />
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="p-16 text-center">
                            <div className="inline-flex items-center justify-center p-5 mb-5 bg-[#f9fafb] rounded-full">
                              <Icon source={OrderIcon} color="subdued" />
                            </div>
                            <div className="max-w-md mx-auto">
                              <Text variant="headingSm" as="h3">{t('no_commission_orders_found')}</Text>
                              <Text color="subdued" as="p" className="mt-2 mb-6">
                                {selectedProgram ?
                                  `No orders found for the ${selectedProgram.program_name} program matching your filters.` :
                                  "No commission orders match your current filters."}
                              </Text>
                              {selectedProgram && (
                                <Button
                                  onClick={() => {
                                    setSelectedProgram(null);
                                    setSelectedProgramId('all');
                                  }}
                                >
                                  {t('view_all_orders')}
                                </Button>
                              )}
                            </div>
                          </div>
                        );
                      })()}
                    </>
                  )}

                </Card>
              </Layout.Section>

              {/* Commission Order Details Modal (add this for viewing order details) */}
              {selectedOrderDetails && (
                <Modal
                  open={!!selectedOrderDetails}
                  onClose={() => setSelectedOrderDetails(null)}
                  title={`${t('order')} #${selectedOrderDetails.order_id} ${t('details')}`}
                  large
                >
                  <Modal.Section>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-6">
                      <div className="bg-[#f6f6f7] p-4 rounded-lg">
                        <Text variant="bodySm" color="subdued">{t('order_date')}</Text>
                        <Text variant="bodyMd" fontWeight="bold">
                          {new Date(selectedOrderDetails.earned_at).toLocaleDateString('en-US', {
                            month: 'long',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </Text>
                      </div>

                      <div className="bg-[#f6f6f7] p-4 rounded-lg">
                        <Text variant="bodySm" color="subdued">{t('commission_amount')}</Text>
                        <Text variant="bodyMd" fontWeight="bold">
                          {formatCurrency(selectedOrderDetails.total_commission)}
                        </Text>
                      </div>

                      <div className="bg-[#f6f6f7] p-4 rounded-lg">
                        <Text variant="bodySm" color="subdued">{t('status')}</Text>
                        <Badge
                          tone={selectedOrderDetails.earnings[0].payroll_id != null ? 'success' : 'critical'}
                          progress={selectedOrderDetails.earnings[0].payroll_id != null ? 'complete' : 'incomplete'}
                        >
                          {selectedOrderDetails.earnings[0].payroll_id != null ? t('paid') : t('unpaid')}
                        </Badge>
                      </div>
                    </div>

                    <div className="mt-4">
                      <Text variant="bodyMd" as="p" fontWeight="bold">
                        {t('commission_breakdown')}
                      </Text>
                      <div className="mt-2">
                        <DataTable
                          columnContentTypes={["text", "text", "text", "text"]}
                          headings={[t('item'), t('orignal_price'), t('commission_type'), t('rate'), t('commission')]}
                          rows={(selectedOrderDetails.earnings || []).map(earning => [
                            <Text variant="bodySm">{earning.product_id ? `Product #${earning.product_id.split('/').pop()}` : 'Product'} (x{earning.quantity || 1})</Text>,
                            <Text variant="bodySm">{earning.original_price || 'N/A'}</Text>,
                            <Text variant="bodySm">{earning.commission_type || 'N/A'}</Text>,
                            <Text variant="bodySm">
                              {earning.commission_type === 'Percentage'
                                ? `${earning.commission_rate}%`
                                : formatCurrency(earning.commission_rate)}
                            </Text>,
                            <Text variant="bodySm" fontWeight="bold">{formatCurrency(earning.commission_earned)}</Text>
                          ])}
                        />
                      </div>
                    </div>
                  </Modal.Section>
                  <Modal.Section>
                    <div className="flex justify-end">
                      <ButtonGroup>
                        <Button onClick={() => setSelectedOrderDetails(null)}>{t('close')}</Button>
                        <Button
                          variant="primary"
                          onClick={() => window.open(`https://${staffData.name}/admin/orders/${selectedOrderDetails.order_id}`, '_blank')}
                        >
                          {t('view_in_shopify')}
                        </Button>
                      </ButtonGroup>
                    </div>
                  </Modal.Section>
                </Modal>
              )}
            </Layout>
          )}
          {selectedTab === 1 && (
            <>
              <div className="mb-4">
                <Card>
                  <div className="p-4 sm:p-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      {/* Total Earnings */}
                      <div className="flex items-center p-4 rounded-lg bg-white border border-[#e1e3e5] hover:border-[#2c6ecb] transition-all duration-200">
                        <div className="p-2 rounded-full bg-[#f1f8ff] mr-3">
                          <Icon source={WalletIcon} color="primary" />
                        </div>
                        <div>
                          <p className="text-[#6d7175] text-sm font-medium">{t('total_earnings')}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <p className="text-2xl font-bold text-[#202223] mt-2 truncate">
                              {currencyData[staffData?.currency]?.symbol || ''}
                              {paymentStats?.totalEarnings ?? 0}
                            </p>
                          </div>
                        </div>
                      </div>
                      {/* Total Paid */}
                      <div className="flex items-center p-4 rounded-lg bg-white border border-[#e1e3e5] hover:border-[#2c6ecb] transition-all duration-200">
                        <div className="p-2 rounded-full bg-[#ebf9eb] mr-3">
                          <Icon source={CheckSmallIcon} color="success" />
                        </div>
                        <div>
                          <p className="text-[#6d7175] text-sm font-medium">{t('paid')}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <p className="text-2xl font-bold text-[#202223] mt-2 truncate">
                              {currencyData[staffData?.currency]?.symbol || ''}
                              {paymentStats?.totalPaid ?? 0}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Total Unpaid */}
                      <div className="flex items-center p-4 rounded-lg bg-white border border-[#e1e3e5] hover:border-[#2c6ecb] transition-all duration-200">
                        <div className="p-2 rounded-full bg-yellow-100 mr-3">
                          <Icon source={AlertBubbleIcon} color="critical" />
                        </div>
                        <div>
                          <p className="text-[#6d7175] text-sm font-medium">{t('unpaid')}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <p className="text-2xl font-bold text-[#202223] mt-2 truncate">
                              {currencyData[staffData?.currency]?.symbol || ''}
                              {paymentStats?.totalUnpaid ?? 0}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Total Transactions */}
                      <div className="flex items-center p-4 rounded-lg bg-white border border-[#e1e3e5] hover:border-[#2c6ecb] transition-all duration-200">
                        <div className="p-2 rounded-full bg-[#f1f8ff] mr-3">
                          <Icon source={TransactionIcon} color="primary" />
                        </div>
                        <div>
                          <p className="text-[#6d7175] text-sm font-medium">{t('total_transactions')}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <p className="text-2xl font-bold text-[#202223] mt-2 truncate">
                              {paymentStats.totalTransactions}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              </div>

              {/* Payment History Table */}
              <Card title={t('recent_transactions')} sectioned>
                <div className="flex justify-end mb-4">
                  <Button
                    icon={ExportIcon}
                    onClick={async () => {
                      try {
                        const response = await axiosClient.get(`/payroll/${id}/export`, {
                          params: {
                            start_date: dateRange.start.toLocaleDateString("en-US"),
                            end_date: dateRange.end.toLocaleDateString("en-US")
                          },
                          responseType: 'blob'
                        });

                        // Create a download link and trigger it
                        const url = window.URL.createObjectURL(new Blob([response.data]));
                        const link = document.createElement('a');
                        link.href = url;

                        // Generate filename
                        const fileName = `Payroll_Transactions_${staffData?.first_name || ''}_${staffData?.last_name || ''}_${new Date().toISOString().split('T')[0]}.xlsx`;
                        link.setAttribute('download', fileName);

                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                        window.URL.revokeObjectURL(url);

                        showToast(t('export_completed'));
                      } catch (error) {
                        showToast(t('export_failed'), true);
                      }
                    }}
                  >
                    {t('export_transactions')}
                  </Button>
                </div>

                <DataTable
                  columnContentTypes={["text", "numeric", "text", "text", "text", "text"]}
                  headings={[
                    t('date'),
                    t('status'),
                    t('amount'),
                    t('payment_method'),
                    t('details'),
                    t('proof'),
                    t('action')
                  ]}
                  rows={paymentStats.recentTransactions.map(transaction => [
                    // Date Column
                    <Text variant="bodyMd" as="span">
                      {new Date(transaction.paid_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </Text>,

                    // Status Column
                    <Badge
                      tone={transaction.status === 'paid' ? 'success' : 'critical'}
                      progress={transaction.status === 'paid' ? 'complete' : 'incomplete'}
                    >
                      {transaction.status === 'paid' ? t('paid').toUpperCase() : t('unpaid').toUpperCase()}
                    </Badge>,

                    // Amount Column
                    <Text variant="bodyMd" as="span" fontWeight="bold">
                      {currencyData[staffData?.currency]?.symbol || ''}{Number(transaction.amount.replace(/,/g, '')).toFixed(2)}
                    </Text>,

                    // Payment Method Column
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {getPaymentMethodIcon(transaction.payment_method)}
                      <Text variant="bodyMd">{transaction.payment_method}</Text>
                    </div>,

                    // Details Column (Payment Type + Period + Notes)
                    <div style={{ maxWidth: '200px' }}>
                      <BlockStack spacing="tight">
                        <Text variant="bodyMd" fontWeight="bold" as="span">
                          {transaction.payment_type === 'bonus' ? t('bonus').charAt(0).toUpperCase() + t('bonus').slice(1) :
                            transaction.payment_type === 'orders' ? t('orders').charAt(0).toUpperCase() + t('orders').slice(1) :
                              transaction.payment_type.charAt(0).toUpperCase() + transaction.payment_type.slice(1)}
                        </Text>

                        {/* Show payment period for salary payments */}
                        {transaction.payment_type === 'salary' && transaction.start_date && transaction.end_date && (
                          <Box
                            background="bg-surface-secondary"
                            borderRadius="100"
                            padding="200"
                          >
                            <div className="flex items-center gap-2">
                              <div style={{ margin: '0px' }}>
                                <Icon source={ClockIcon} color="base" />
                              </div>
                              <Text variant="bodySm" color="subdued">
                                {new Date(transaction.start_date).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric'
                                })}
                                {' - '}
                                {new Date(transaction.end_date).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric'
                                })}
                              </Text>
                            </div>
                          </Box>
                        )}

                        {/* Show bonus note only for bonus payments */}
                        {transaction.payment_type === 'bonus' && transaction.bonus_details && (
                          <Box
                            background="bg-surface-secondary"
                            borderRadius="100"
                            padding="200"
                          >
                            <Text variant="bodySm" color="subdued">
                              {transaction.bonus_details}
                            </Text>
                          </Box>
                        )}

                        {/* Show commission orders if available */}
                        {transaction.payment_type === 'commission' && transaction.commission_orders && (
                          <Box
                            background="bg-surface-secondary"
                            borderRadius="100"
                            padding="200"
                          >
                            <Text variant="bodySm" color="subdued">
                              {`${transaction.commission_orders.length} ${t('orders')}`}
                            </Text>
                          </Box>
                        )}

                        {/* Show additional note if exists */}
                        {transaction.additional_note && (
                          <Text variant="bodySm" color="subdued">
                            {transaction.additional_note}
                          </Text>
                        )}
                      </BlockStack>
                    </div>,

                    // Payment Proof Column
                    <div style={{ minWidth: '100px' }}>
                      {transaction.payment_proof ? (
                        <div
                          style={{
                            position: 'relative',
                            cursor: 'pointer'
                          }}
                          onClick={() => {
                            setSelectedProof(`/storage/${transaction.payment_proof}`);
                            setIsProofModalOpen(true);
                          }}
                        >
                          <img
                            src={`/storage/${transaction.payment_proof}`}
                            alt={t('payment_proof')}
                            style={{
                              width: '60px',
                              height: '60px',
                              objectFit: 'cover',
                              borderRadius: '4px',
                              border: '1px solid #e1e3e5'
                            }}
                          />
                          <div
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              background: 'rgba(0,0,0,0.05)',
                              borderRadius: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              opacity: 0,
                              transition: 'opacity 0.2s',
                              ':hover': {
                                opacity: 1
                              }
                            }}
                          >
                            <Icon source={ViewIcon} color="base" />
                          </div>
                        </div>
                      ) : (
                        <Text variant="bodySm" color="subdued">-</Text>
                      )}
                    </div>,

                    // Action Column
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      <Button
                        icon={DeleteIcon}
                        tone="critical"
                        variant="plain"
                        onClick={() => handleDeletePayroll(transaction)}
                        accessibilityLabel={t('delete_transaction')}
                      />
                    </div>
                  ])}
                  pagination={{
                    hasNext: paymentStats.recentTransactions.length < paymentStats.totalTransactions,
                    onNext: () => {
                      /* Implement pagination logic */
                    },
                  }}
                />

                {/* Payment Proof Modal */}
                {isProofModalOpen && (
                  <Modal
                    open={isProofModalOpen}
                    onClose={() => setIsProofModalOpen(false)}
                    title={t('payment_proof')}
                    large
                  >
                    <Modal.Section>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: '20px'
                      }}>
                        <img
                          src={selectedProof}
                          alt={t('payment_proof')}
                          style={{
                            maxWidth: '100%',
                            maxHeight: '80vh',
                            objectFit: 'contain'
                          }}
                        />
                      </div>
                    </Modal.Section>
                  </Modal>
                )}
              </Card>
            </>
          )}
        </div>

      </Page>
      {/* Edit Clock Time Modal */}
      <Modal
        open={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title={selectedClock?.end_at ? t('edit_clock_times') : t('update_clock_times')}
        primaryAction={{
          content: isSaving ? t('saving') : t('save'),
          onAction: handleSaveClockTimes,
          loading: isSaving,
          disabled: isSaving,
        }}
        secondaryActions={[
          {
            content: t('cancel'),
            onAction: () => setIsEditModalOpen(false),
          },
        ]}
      >
        <Modal.Section>
          <FormLayout>
            <TextField
              label={t('first_in_time')}
              type="time"
              value={editedStartTime ? editedStartTime.substring(0, 5) : ''}
              onChange={(value) => setEditedStartTime(`${value}`)}
              autoComplete="off"
              helpText={t('edit_clock_in_time')}
            />

            <TextField
              label={t('last_out_time')}
              type="time"
              value={editedEndTime ? editedEndTime.substring(0, 5) : ''}
              onChange={(value) => setEditedEndTime(`${value}`)}
              autoComplete="off"
              helpText={!selectedClock?.end_at ? t('add_clock_out_time') : t('edit_clock_out_time')}
            />

            {editedStartTime && editedEndTime && (
              <Box paddingBlockStart="400">
                <Text as="p">
                  {t('total_hours_label')}: <strong>{calculateDayTotal(editedStartTime, editedEndTime)}</strong>
                </Text>
              </Box>
            )}
          </FormLayout>
        </Modal.Section>
      </Modal>

      {/* View Entries Modal */}
      <Modal
        open={isViewEntriesModalOpen}
        onClose={() => {
          setIsViewEntriesModalOpen(false);
          setDateEntries([]);
        }}
        title={`${t('time_entries') || 'Time Entries'} - ${selectedClock?.formatted_date || selectedClock?.current_date}`}
        large
        secondaryActions={[
          {
            content: t('add_time_entry') || 'Add Time Entry',
            icon: PlusIcon,
            onAction: () => {
              // Pre-fill the date from the selected clock
              if (selectedClock?.current_date) {
                // Parse the date string correctly to avoid timezone issues
                const dateParts = selectedClock.current_date.split('-');
                const dateObj = new Date(dateParts[0], dateParts[1] - 1, dateParts[2]);
                setTimeEntryDate({
                  start: dateObj,
                  end: dateObj
                });
              }
              // Set default time to current time
              const now = new Date();
              const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
              setTimeEntryTime(currentTime);
              setIsViewEntriesModalOpen(false);
              setIsTimeEntryModalOpen(true);
            }
          }
        ]}
      >
        <Modal.Section>
          {loadingEntries ? (
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <Spinner size="large" />
              <div style={{ marginTop: '16px' }}>
                <Text as="p" variant="bodyMd" tone="subdued">
                  {t('loading_entries') || 'Loading entries...'}
                </Text>
              </div>
            </div>
          ) : dateEntries.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <div style={{
                width: '64px',
                height: '64px',
                margin: '0 auto 16px',
                borderRadius: '50%',
                backgroundColor: '#f3f4f6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '32px'
              }}>
                📋
              </div>
              <Text as="p" variant="bodyMd" tone="subdued">
                {t('no_entries_found') || 'No entries found for this date'}
              </Text>
              <div style={{ marginTop: '16px' }}>
                <Button
                  variant="primary"
                  icon={PlusIcon}
                  onClick={() => {
                    // Pre-fill the date from the selected clock
                    if (selectedClock?.current_date) {
                      // Parse the date string correctly to avoid timezone issues
                      const dateParts = selectedClock.current_date.split('-');
                      const dateObj = new Date(dateParts[0], dateParts[1] - 1, dateParts[2]);
                      setTimeEntryDate({
                        start: dateObj,
                        end: dateObj
                      });
                    }
                    // Set default time to current time
                    const now = new Date();
                    const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
                    setTimeEntryTime(currentTime);
                    setIsViewEntriesModalOpen(false);
                    setIsTimeEntryModalOpen(true);
                  }}
                >
                  {t('add_first_entry') || 'Add First Entry'}
                </Button>
              </div>
            </div>
          ) : (
            <div style={{ maxHeight: '500px', overflowY: 'auto', padding: '4px' }}>
              <div style={{
                display: 'grid',
                gap: '12px',
                gridTemplateColumns: '1fr'
              }}>
                {dateEntries.map((entry, index) => {
                  const isClockIn = entry.event_type === 'clock-in';
                  const isClockOut = entry.event_type === 'clock-out';

                  return (
                    <div
                      key={entry.id || index}
                      style={{
                        padding: '16px 20px',
                        backgroundColor: 'white',
                        borderRadius: '12px',
                        border: '1px solid #e5e7eb',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                        transition: 'all 0.2s ease',
                        cursor: 'default'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.07)';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.05)';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flex: 1 }}>
                          {/* Photo Avatar or Icon */}
                          {entry.photo ? (
                            <PhotoAvatar
                              photo={entry.photo}
                              label={`${isClockIn ? t('clock_in') : isClockOut ? t('clock_out') : t('break')} - ${entry.start_at || entry.end_at}`}
                              size="medium"
                            />
                          ) : (
                            <div
                              style={{
                                width: '48px',
                                height: '48px',
                                borderRadius: '12px',
                                backgroundColor: isClockIn ? '#eff6ff' : isClockOut ? '#fef2f2' : '#fefce8',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '20px',
                                flexShrink: 0
                              }}
                            >
                              {isClockIn ? '🟢' : isClockOut ? '🔴' : '⏸️'}
                            </div>
                          )}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                              <Text as="span" variant="bodyMd" fontWeight="semibold">
                                {isClockIn ? t('clock_in') || 'Clock In' :
                                  isClockOut ? t('clock_out') || 'Clock Out' :
                                    t('break') || 'Break'}
                              </Text>
                              <Badge tone={isClockIn ? 'info' : isClockOut ? 'critical' : 'warning'} size="small">
                                {entry.event_type}
                              </Badge>
                              {entry.photo && (
                                <Badge tone="success" size="small">
                                  📷 Photo
                                </Badge>
                              )}
                            </div>
                            {gpsLocationEnabled && entry.latitude != null && entry.longitude != null && (
                              <div style={{ marginTop: '6px' }}>
                                <GpsCoordinateLink
                                  latitude={entry.latitude}
                                  longitude={entry.longitude}
                                  label={
                                    isClockIn
                                      ? t('clock_in_gps')
                                      : isClockOut
                                        ? t('clock_out_gps')
                                        : t('gps_coordinates')
                                  }
                                />
                              </div>
                            )}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                              {/* Show start time for clock-in and break */}
                              {(isClockIn || entry.event_type === 'break') && entry.start_at && (
                                <Text as="span" variant="bodySm" tone="subdued">
                                  🕐 {t('start')}: {entry.start_at}
                                </Text>
                              )}
                              {/* Show end time for clock-out */}
                              {isClockOut && entry.end_at && (
                                <Text as="span" variant="bodySm" tone="subdued">
                                  🕐 {t('end')}: {entry.end_at}
                                </Text>
                              )}
                              {/* Show both start and end for break if end_at exists */}
                              {entry.event_type === 'break' && entry.end_at && (
                                <Text as="span" variant="bodySm" tone="subdued">
                                  → {t('end')}: {entry.end_at}
                                </Text>
                              )}
                              {/* Timezone this punch was recorded in */}
                              {entry.timezone && (isClockIn || isClockOut) && (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                                  <span style={{ width: '13px', height: '13px', display: 'inline-flex' }}>
                                    <Icon source={GlobeIcon} color="subdued" />
                                  </span>
                                  <Text as="span" variant="bodySm" tone="subdued">
                                    {entry.timezone}
                                  </Text>
                                </span>
                              )}
                              {/* Show total time worked for clock-out */}
                              {(entry.session_time || entry.total_time_worked) && isClockOut && (
                                <div style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  padding: '2px 8px',
                                  backgroundColor: '#d1fae5',
                                  borderRadius: '6px'
                                }}>
                                  <Text as="span" variant="bodySm" fontWeight="medium" tone="success">
                                    ⏱️ {entry.session_time || entry.total_time_worked}
                                  </Text>
                                </div>
                              )}
                            </div>
                            {isClockOut && entry.note && (
                              <div style={{ marginTop: '8px' }}>
                                <Text as="p" variant="bodySm" tone="subdued">
                                  {t('clock_out_note')}: {entry.note}
                                </Text>
                              </div>
                            )}
                          </div>
                        </div>
                        {/* Edit and Delete Buttons */}
                        <div style={{ display: 'flex', gap: '8px', marginLeft: '8px' }}>
                          <Button
                            size="slim"
                            icon={EditIcon}
                            onClick={() => handleEditEntry(entry)}
                          />
                          <Button
                            size="slim"
                            tone="critical"
                            icon={DeleteIcon}
                            onClick={() => handleDeleteEntry(entry)}
                            loading={isDeletingEntry}
                            disabled={isDeletingEntry}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </Modal.Section>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title={t('delete_clock_entries') || 'Delete Clock Entries'}
        primaryAction={{
          content: deleteLoading ? t('deleting') || 'Deleting...' : t('delete') || 'Delete',
          onAction: handleDeleteConfirm,
          destructive: true,
          loading: deleteLoading,
        }}
        secondaryActions={[
          {
            content: t('cancel') || 'Cancel',
            onAction: () => setIsDeleteModalOpen(false),
          },
        ]}
      >
        <Modal.Section>
          <Text as="p">
            {t('delete_clock_entries_confirmation') || 'Are you sure you want to delete all clock entries for'} <strong>{selectedClock?.formatted_date || selectedClock?.current_date}</strong>?
          </Text>
          <Box paddingBlockStart="400">
            <Text as="p" tone="critical">
              {t('delete_warning') || 'This action cannot be undone. All clock-in, clock-out, and break records for this date will be permanently deleted.'}
            </Text>
          </Box>
        </Modal.Section>
      </Modal>

      {/* New Time Entry Modal with Tabs */}
      <Modal
        open={isTimeEntryModalOpen}
        onClose={() => {
          setIsTimeEntryModalOpen(false);
          setTimeEntryType('clock-in');
          setTimeEntryTime('');
          setEditingEntry(null);
        }}
        title={editingEntry ? (t('edit_time_entry') || 'Edit Time Entry') : (t('add_time_entry') || 'Add Time Entry')}
        primaryAction={{
          content: isAddingTimeEntry ? (editingEntry ? t('updating') : t('adding')) : (editingEntry ? t('update') : t('save')),
          onAction: handleTimeEntrySubmit,
          loading: isAddingTimeEntry,
          disabled: isAddingTimeEntry || !timeEntryTime
        }}
        secondaryActions={[
          {
            content: t('cancel'),
            onAction: () => {
              setIsTimeEntryModalOpen(false);
              setTimeEntryType('clock-in');
              setTimeEntryTime('');
              setEditingEntry(null);
            },
          },
        ]}
      >
        <Modal.Section>
          <FormLayout>
            {/* Entry Type Tabs */}
            <div style={{ marginBottom: '20px' }}>
              <Text variant="bodyMd" as="p" fontWeight="semibold" tone="subdued">
                {t('entry_type') || 'Entry Type'}
              </Text>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '8px',
                marginTop: '12px'
              }}>
                {/* Clock In Tab */}
                <div
                  onClick={() => setTimeEntryType('clock-in')}
                  style={{
                    padding: '16px',
                    borderRadius: '12px',
                    border: timeEntryType === 'clock-in' ? '2px solid #2c6ecb' : '2px solid #e5e7eb',
                    backgroundColor: timeEntryType === 'clock-in' ? '#eff6ff' : 'white',
                    cursor: 'pointer',
                    textAlign: 'center',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <div style={{ fontSize: '24px' }}>🟢</div>
                  <Text variant="bodyMd" fontWeight={timeEntryType === 'clock-in' ? 'semibold' : 'regular'}>
                    {t('clock_in') || 'In'}
                  </Text>
                </div>

                {/* Break Tab */}
                <div
                  onClick={() => setTimeEntryType('break')}
                  style={{
                    padding: '16px',
                    borderRadius: '12px',
                    border: timeEntryType === 'break' ? '2px solid #eab308' : '2px solid #e5e7eb',
                    backgroundColor: timeEntryType === 'break' ? '#fefce8' : 'white',
                    cursor: 'pointer',
                    textAlign: 'center',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <div style={{ fontSize: '24px' }}>⏸️</div>
                  <Text variant="bodyMd" fontWeight={timeEntryType === 'break' ? 'semibold' : 'regular'}>
                    {t('break') || 'Break'}
                  </Text>
                </div>

                {/* Clock Out Tab */}
                <div
                  onClick={() => setTimeEntryType('clock-out')}
                  style={{
                    padding: '16px',
                    borderRadius: '12px',
                    border: timeEntryType === 'clock-out' ? '2px solid #ef4444' : '2px solid #e5e7eb',
                    backgroundColor: timeEntryType === 'clock-out' ? '#fef2f2' : 'white',
                    cursor: 'pointer',
                    textAlign: 'center',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <div style={{ fontSize: '24px' }}>🔴</div>
                  <Text variant="bodyMd" fontWeight={timeEntryType === 'clock-out' ? 'semibold' : 'regular'}>
                    {t('clock_out') || 'Out'}
                  </Text>
                </div>
              </div>
            </div>

            {/* Time Input - Different layout for break vs other types */}
            {timeEntryType === 'break' ? (
              // Break Time Fields - Show both start and end in a grouped container
              <div style={{
                padding: '20px',
                backgroundColor: '#fefce8',
                borderRadius: '12px',
                border: '2px solid #eab308'
              }}>
                <div style={{ marginBottom: '16px' }}>
                  <Text variant="bodyMd" as="p" fontWeight="semibold" tone="subdued">
                    ⏸️ {t('break_time') || 'Break Time'}
                  </Text>
                  <Text variant="bodySm" as="p" tone="subdued" style={{ marginTop: '4px' }}>
                    {t('enter_break_start_and_end_times') || 'Enter when the break started and ended'}
                  </Text>
                </div>

                <div style={{ display: 'grid', gap: '16px' }}>
                  {/* Break Start Time */}
                  <div>
                    <TextField
                      label={t('start_time') || 'Start Time'}
                      type="time"
                      value={timeEntryTime ? timeEntryTime.substring(0, 5) : ''}
                      onChange={(value) => setTimeEntryTime(`${value}`)}
                      autoComplete="off"
                      required
                      prefix={
                        <div className="p-1 rounded-full bg-[#fef3c7]">
                          <Icon source={ClockIcon} color="warning" />
                        </div>
                      }
                    />
                  </div>

                  {/* Break End Time */}
                  <div>
                    <TextField
                      label={t('end_time') || 'End Time'}
                      type="time"
                      value={breakEndTime ? breakEndTime.substring(0, 5) : ''}
                      onChange={(value) => setBreakEndTime(`${value}`)}
                      autoComplete="off"
                      required
                      prefix={
                        <div className="p-1 rounded-full bg-[#fef3c7]">
                          <Icon source={ClockIcon} color="warning" />
                        </div>
                      }
                    />
                  </div>
                </div>
              </div>
            ) : (
              // Clock In/Out Time Field - Single time input
              <div style={{
                padding: '16px',
                backgroundColor: '#f9fafb',
                borderRadius: '12px',
                border: '1px solid #e5e7eb'
              }}>
                <TextField
                  label={t('time') || 'Time'}
                  type="time"
                  value={timeEntryTime ? timeEntryTime.substring(0, 5) : ''}
                  onChange={(value) => setTimeEntryTime(`${value}`)}
                  autoComplete="off"
                  helpText={t('select_time_for_entry') || 'Select the time for this entry'}
                  required
                  prefix={
                    <div className="p-1 rounded-full bg-[#f1f8ff]">
                      <Icon source={ClockIcon} color="primary" />
                    </div>
                  }
                />
              </div>
            )}

            {/* Date Picker */}
            <div>
              <Text variant="bodyMd" as="p" fontWeight="semibold" tone="subdued">
                {t('date') || 'Date'}
              </Text>
              <div className="flex items-center gap-2 mt-2">
                <div
                  className="flex-grow p-3 border rounded-md cursor-pointer hover:border-[#2c6ecb] transition-all"
                  style={{
                    backgroundColor: '#f9fafb',
                    borderColor: '#e5e7eb'
                  }}
                  onClick={() => {
                    setShowTimeEntryDatePicker(!showTimeEntryDatePicker);
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="p-1 rounded-full bg-[#f1f8ff]">
                        <Icon source={CalendarIcon} color="primary" />
                      </div>
                      <Text variant="bodyMd">
                        {timeEntryDate.start.toLocaleDateString('en-US', {
                          weekday: 'short',
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </Text>
                    </div>
                    <div style={{ margin: '0px' }}>
                      <Icon source={ChevronDownIcon} color="base" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Collapsible date picker */}
              <Collapsible
                open={showTimeEntryDatePicker}
                id="time-entry-date-picker-collapsible"
                transition={{ duration: '150ms', timingFunction: 'ease' }}
              >
                <Card>
                  <div className="p-2">
                    <DatePicker
                      month={month}
                      year={year}
                      onChange={(dates) => {
                        setTimeEntryDate(dates);
                        setShowTimeEntryDatePicker(false);
                      }}
                      onMonthChange={(month, year) => setDate({ month, year })}
                      selected={timeEntryDate}
                      allowRange={false}
                    />
                  </div>
                </Card>
              </Collapsible>
            </div>

            {/* Location Selection */}
            <Select
              label={t('location') || 'Location'}
              options={
                locations.length > 0
                  ? locations.map(loc => ({ label: loc.name, value: loc.id.toString() }))
                  : [{ label: t('loading_locations'), value: '' }]
              }
              value={selectedLocationId}
              onChange={setSelectedLocationId}
              disabled={isLoadingLocations}
              helpText={t('select_location_help') || 'Select the location for this entry'}
              placeholder={isLoadingLocations ? t('loading_locations') : t('select_location')}
            />

            {/* Summary Box - Only show for clock-in and clock-out, not for break */}
            {timeEntryType !== 'break' && (
              <Box paddingBlockStart="400" background="bg-surface-secondary" borderRadius="300" padding="300">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text as="span" variant="bodySm" tone="subdued">
                      {t('entry_type') || 'Entry Type'}:
                    </Text>
                    <Badge tone={timeEntryType === 'clock-in' ? 'info' : 'critical'}>
                      {timeEntryType === 'clock-in' ? t('clock_in') || 'Clock In' : t('clock_out') || 'Clock Out'}
                    </Badge>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text as="span" variant="bodySm" tone="subdued">
                      {t('time') || 'Time'}:
                    </Text>
                    <Text as="span" variant="bodyMd" fontWeight="semibold">
                      {timeEntryTime || '--:--'}
                    </Text>
                  </div>
                </div>
              </Box>
            )}
          </FormLayout>
        </Modal.Section>
      </Modal>

      {/* Manual Clock Entry Modal */}
      <Modal
        open={isManualEntryModalOpen}
        onClose={() => setIsManualEntryModalOpen(false)}
        title={t('add_manual_clock_entry')}
        primaryAction={{
          content: isAddingManualEntry ? t('adding') : t('add_entry'),
          onAction: handleManualEntrySubmit,
          loading: isAddingManualEntry,
          disabled: isAddingManualEntry || !manualClockInTime
        }}
        secondaryActions={[
          {
            content: t('cancel'),
            onAction: () => setIsManualEntryModalOpen(false),
          },
        ]}
      >
        <Modal.Section>
          <FormLayout>
            {/* Enhanced date picker with preview */}
            <div className="mb-4">
              <Text variant="bodyMd" as="p" fontWeight="semibold">
                {t('select_date')}
              </Text>
              <div className="flex items-center gap-2 mt-2 mb-4">
                <div
                  className="flex-grow p-3 border rounded-md cursor-pointer hover:border-[#2c6ecb] transition-all"
                  onClick={() => {
                    // Toggle a popover or state to show the date picker
                    setShowDatePicker(!showDatePicker);
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="p-1 rounded-full bg-[#f1f8ff]">
                        <Icon source={CalendarIcon} color="primary" />
                      </div>
                      <Text variant="bodyMd">
                        {selectedDate.start.toLocaleDateString('en-US', {
                          weekday: 'short',
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </Text>
                    </div>
                    <div style={{ margin: '0px' }}>
                      <Icon source={ChevronDownIcon} color="base" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Collapsible date picker that only shows when needed */}
              <Collapsible
                open={showDatePicker}
                id="date-picker-collapsible"
                transition={{ duration: '150ms', timingFunction: 'ease' }}
              >
                <Card>
                  <div className="p-2">
                    <DatePicker
                      month={month}
                      year={year}
                      onChange={(dates) => {
                        setSelectedDate(dates);
                        setShowDatePicker(false); // Hide the picker after selection
                      }}
                      onMonthChange={(month, year) => setDate({ month, year })}
                      selected={selectedDate}
                      allowRange={false}
                    />
                  </div>
                </Card>
              </Collapsible>
            </div>

            <Select
              label={t('location')}
              options={
                locations.length > 0
                  ? locations.map(loc => ({ label: loc.name, value: loc.id.toString() }))
                  : [{ label: t('loading_locations'), value: '' }]
              }
              value={selectedLocationId}
              onChange={setSelectedLocationId}
              disabled={isLoadingLocations}
              helpText={t('select_location_help')}
              placeholder={isLoadingLocations ? t('loading_locations') : t('select_location')}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <TextField
                label={t('first_in_time')}
                type="time"
                value={manualClockInTime ? manualClockInTime.substring(0, 5) : ''}
                onChange={(value) => setManualClockInTime(`${value}`)}
                autoComplete="off"
                helpText={t('enter_clock_in_time')}
                required
                prefix={
                  <div className="p-1 rounded-full bg-[#ebf9eb]">
                    <Icon source={ClockIcon} color="success" />
                  </div>
                }
              />

              <TextField
                label={t('last_out_time')}
                type="time"
                value={manualClockOutTime ? manualClockOutTime.substring(0, 5) : ''}
                onChange={(value) => setManualClockOutTime(`${value}`)}
                autoComplete="off"
                helpText={t('leave_empty_if_only_clocking_in')}
                prefix={
                  <div className="p-1 rounded-full bg-yellow-100">
                    <Icon source={ClockIcon} color="critical" />
                  </div>
                }
              />
            </div>

            {manualClockInTime && manualClockOutTime && (
              <Box paddingBlockStart="400" background="bg-surface-secondary" borderRadius="300" padding="300">
                <div className="flex items-center justify-between">
                  <Text as="p" fontWeight="medium">
                    {t('total_hours_label')}:
                  </Text>
                  <Badge size="large">
                    {calculateDayTotal(manualClockInTime, manualClockOutTime)}
                  </Badge>
                </div>
              </Box>
            )}
          </FormLayout>
        </Modal.Section>
      </Modal>

      {/* Delete Payroll Confirmation Modal */}
      <Modal
        open={isDeletePayrollModalOpen}
        onClose={() => setIsDeletePayrollModalOpen(false)}
        title={t('delete_payroll_transaction') || 'Delete Payroll Transaction'}
        primaryAction={{
          content: isDeletingPayroll ? t('deleting') || 'Deleting...' : t('delete') || 'Delete',
          onAction: confirmDeletePayroll,
          destructive: true,
          loading: isDeletingPayroll,
        }}
        secondaryActions={[
          {
            content: t('cancel') || 'Cancel',
            onAction: () => setIsDeletePayrollModalOpen(false),
          },
        ]}
      >
        <Modal.Section>
          <Text as="p">
            {t('delete_payroll_confirmation_message') || 'Are you sure you want to delete this payroll transaction?'}
          </Text>
          <Box paddingBlockStart="400">
            <Text as="p" tone="critical">
              {t('delete_warning') || 'This action cannot be undone. The transaction record will be permanently removed.'}
            </Text>
          </Box>
        </Modal.Section>
      </Modal>
    </>
  );
};

export default StaffDetails;